import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth, apiClient } from "@/lib/api";
import { useProfile } from "@/lib/ProfileContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { ConnectionSuccessModal, DisconnectConfirmModal } from "@/components/ConnectionModals";

// Common timezones grouped by region
const TIMEZONE_OPTIONS = [
  { group: "Americas", timezones: [
    { value: "America/New_York", label: "Eastern Time (New York)" },
    { value: "America/Chicago", label: "Central Time (Chicago)" },
    { value: "America/Denver", label: "Mountain Time (Denver)" },
    { value: "America/Los_Angeles", label: "Pacific Time (Los Angeles)" },
    { value: "America/Toronto", label: "Eastern Time (Toronto)" },
    { value: "America/Vancouver", label: "Pacific Time (Vancouver)" },
    { value: "America/Sao_Paulo", label: "Brasilia Time (São Paulo)" },
    { value: "America/Mexico_City", label: "Central Time (Mexico City)" },
  ]},
  { group: "Europe", timezones: [
    { value: "Europe/London", label: "GMT (London)" },
    { value: "Europe/Paris", label: "Central European (Paris)" },
    { value: "Europe/Berlin", label: "Central European (Berlin)" },
    { value: "Europe/Rome", label: "Central European (Rome)" },
    { value: "Europe/Madrid", label: "Central European (Madrid)" },
    { value: "Europe/Amsterdam", label: "Central European (Amsterdam)" },
    { value: "Europe/Moscow", label: "Moscow Time" },
  ]},
  { group: "Asia", timezones: [
    { value: "Asia/Dubai", label: "Gulf Standard (Dubai)" },
    { value: "Asia/Kolkata", label: "India Standard (Mumbai)" },
    { value: "Asia/Singapore", label: "Singapore Time" },
    { value: "Asia/Hong_Kong", label: "Hong Kong Time" },
    { value: "Asia/Tokyo", label: "Japan Standard (Tokyo)" },
    { value: "Asia/Shanghai", label: "China Standard (Shanghai)" },
    { value: "Asia/Seoul", label: "Korea Standard (Seoul)" },
  ]},
  { group: "Pacific", timezones: [
    { value: "Australia/Sydney", label: "Australian Eastern (Sydney)" },
    { value: "Australia/Melbourne", label: "Australian Eastern (Melbourne)" },
    { value: "Australia/Perth", label: "Australian Western (Perth)" },
    { value: "Pacific/Auckland", label: "New Zealand (Auckland)" },
  ]},
  { group: "Africa", timezones: [
    { value: "Africa/Cairo", label: "Eastern European (Cairo)" },
    { value: "Africa/Lagos", label: "West Africa (Lagos)" },
    { value: "Africa/Johannesburg", label: "South Africa (Johannesburg)" },
  ]},
];

// Currency options
const CURRENCY_OPTIONS = [
  { value: "USD", label: "USD - US Dollar ($)", symbol: "$" },
  { value: "CAD", label: "CAD - Canadian Dollar ($)", symbol: "$" },
  { value: "EUR", label: "EUR - Euro (€)", symbol: "€" },
  { value: "GBP", label: "GBP - British Pound (£)", symbol: "£" },
  { value: "INR", label: "INR - Indian Rupee (₹)", symbol: "₹" },
  { value: "AUD", label: "AUD - Australian Dollar ($)", symbol: "$" },
  { value: "JPY", label: "JPY - Japanese Yen (¥)", symbol: "¥" },
  { value: "CNY", label: "CNY - Chinese Yuan (¥)", symbol: "¥" },
  { value: "SGD", label: "SGD - Singapore Dollar ($)", symbol: "$" },
  { value: "AED", label: "AED - UAE Dirham (د.إ)", symbol: "د.إ" },
  { value: "SAR", label: "SAR - Saudi Riyal (ر.س)", symbol: "ر.س" },
  { value: "BRL", label: "BRL - Brazilian Real (R$)", symbol: "R$" },
  { value: "MXN", label: "MXN - Mexican Peso ($)", symbol: "$" },
  { value: "NZD", label: "NZD - New Zealand Dollar ($)", symbol: "$" },
];
import usePushNotifications from "@/hooks/usePushNotifications";
import { 
  User, 
  CreditCard, 
  Bell, 
  Shield, 
  Palette,
  Save,
  Loader2,
  Link2,
  Facebook,
  Instagram,
  CheckCircle,
  AlertCircle,
  ExternalLink,
  Gift,
  Star,
  TrendingUp,
  Award,
  ArrowRight,
  BellRing,
  BellOff,
  Sparkles,
  Camera,
  Building2,
  Upload,
  Trash2,
  Globe,
  DollarSign,
  Clock,
  Edit2,
  X,
  Plus,
  Check,
  Info,
  Mail,
  AlertTriangle
} from "lucide-react";

// Push Notification Toggle Component
function PushNotificationToggle() {
  const { 
    isSupported, 
    isSubscribed, 
    isLoading, 
    permission, 
    error,
    subscribe, 
    unsubscribe 
  } = usePushNotifications();

  const handleToggle = async () => {
    if (isSubscribed) {
      const success = await unsubscribe();
      if (success) {
        toast.success("Push notifications disabled");
      } else {
        toast.error("Failed to disable notifications");
      }
    } else {
      const success = await subscribe();
      if (success) {
        toast.success("Push notifications enabled!");
      } else if (permission === 'denied') {
        toast.error("Please enable notifications in your browser settings");
      } else {
        toast.error("Failed to enable notifications");
      }
    }
  };

  if (!isSupported) {
    return (
      <div className="p-4 rounded-xl bg-white/5 border border-white/10">
        <div className="flex items-center gap-3 text-viraly-muted">
          <BellOff className="w-5 h-5" />
          <p className="text-sm">Push notifications are not supported in this browser</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between p-4 rounded-xl bg-white/5 border border-white/10">
        <div className="flex items-center gap-3">
          {isSubscribed ? (
            <div className="w-10 h-10 rounded-xl bg-viraly-success/20 flex items-center justify-center">
              <BellRing className="w-5 h-5 text-viraly-success" />
            </div>
          ) : (
            <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
              <Bell className="w-5 h-5 text-viraly-muted" />
            </div>
          )}
          <div>
            <p className="font-medium">{isSubscribed ? 'Notifications Enabled' : 'Enable Notifications'}</p>
            <p className="text-xs text-viraly-muted">
              {isSubscribed 
                ? 'You will receive push notifications on this device' 
                : 'Get notified when content is ready, posts are published, etc.'}
            </p>
          </div>
        </div>
        <Button
          onClick={handleToggle}
          disabled={isLoading}
          className={isSubscribed ? 'btn-secondary' : 'btn-primary'}
        >
          {isLoading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : isSubscribed ? (
            'Disable'
          ) : (
            'Enable'
          )}
        </Button>
      </div>

      {permission === 'denied' && (
        <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-sm">
          <p className="text-red-400">
            Notifications are blocked. Please enable them in your browser settings to receive push notifications.
          </p>
        </div>
      )}

      {error && permission !== 'denied' && (
        <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20 text-sm">
          <p className="text-yellow-400">{error}</p>
        </div>
      )}

      {/* What notifications you'll receive */}
      {isSubscribed && (
        <div className="p-4 rounded-xl bg-white/5">
          <p className="text-sm font-medium mb-2">You'll be notified when:</p>
          <ul className="text-xs text-viraly-muted space-y-1">
            <li>• Your AI-generated content is ready</li>
            <li>• Scheduled posts are published</li>
            <li>• You're running low on credits</li>
            <li>• Your credits are about to expire</li>
          </ul>
        </div>
      )}
    </div>
  );
}

// Notification Toggle with Disable Warning
function NotificationToggleWithWarning({ item }) {
  const [enabled, setEnabled] = useState(true);
  const [showWarning, setShowWarning] = useState(false);
  
  const handleToggle = () => {
    if (enabled) {
      // User is trying to disable - show warning
      setShowWarning(true);
    } else {
      // User is enabling - just enable
      setEnabled(true);
      toast.success(`${item.label} enabled`);
    }
  };
  
  const confirmDisable = () => {
    setEnabled(false);
    setShowWarning(false);
    toast.info(`${item.label} disabled`);
  };
  
  return (
    <>
      <div className="flex items-center justify-between p-3 rounded-xl bg-white/5 mb-2">
        <div className="flex-1">
          <p className="font-medium text-sm">{item.label}</p>
          <p className="text-xs text-viraly-muted">{item.desc}</p>
        </div>
        <label className="relative inline-flex items-center cursor-pointer ml-4">
          <input 
            type="checkbox" 
            className="sr-only peer" 
            checked={enabled}
            onChange={handleToggle}
          />
          <div className="w-11 h-6 bg-white/10 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-viraly-orange"></div>
        </label>
      </div>
      
      {/* Warning Dialog */}
      {showWarning && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-viraly-card border border-white/10 rounded-2xl p-6 max-w-md mx-4 shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-yellow-500/20 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-yellow-400" />
              </div>
              <div>
                <h3 className="font-heading text-lg font-bold">Disable {item.label}?</h3>
                <p className="text-sm text-viraly-muted">This will affect your experience</p>
              </div>
            </div>
            <p className="text-sm text-yellow-400 bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-3 mb-4">
              ⚠️ {item.warning}
            </p>
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setShowWarning(false)}
              >
                Keep Enabled
              </Button>
              <Button
                className="flex-1 bg-yellow-600 hover:bg-yellow-700"
                onClick={confirmDisable}
              >
                Disable Anyway
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// Loyalty tiers configuration
const LOYALTY_TIERS = [
  { name: "Bronze", threshold: 50, discount: 5, color: "amber" },
  { name: "Silver", threshold: 100, discount: 10, color: "gray" },
  { name: "Gold", threshold: 200, discount: 15, color: "yellow" },
  { name: "Platinum", threshold: 500, discount: 20, color: "purple" },
  { name: "Diamond", threshold: 1000, discount: 25, color: "cyan" }
];

// Profile Selection Modal for Plan Downgrade
function DowngradeProfileSelectionModal({ isOpen, onClose, profiles, allowedCount, onConfirm }) {
  const [selectedProfiles, setSelectedProfiles] = useState([]);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    // Pre-select the default profile
    if (profiles && profiles.length > 0) {
      const defaultProfile = profiles.find(p => p.is_default);
      if (defaultProfile) {
        setSelectedProfiles([defaultProfile.id]);
      }
    }
  }, [profiles]);

  const toggleProfile = (profileId) => {
    const profile = profiles.find(p => p.id === profileId);
    if (profile?.is_default) {
      toast.error("Default profile cannot be deselected");
      return;
    }

    setSelectedProfiles(prev => {
      if (prev.includes(profileId)) {
        return prev.filter(id => id !== profileId);
      }
      if (prev.length >= allowedCount) {
        toast.error(`You can only keep ${allowedCount} profile(s) on your current plan`);
        return prev;
      }
      return [...prev, profileId];
    });
  };

  const handleConfirm = async () => {
    if (selectedProfiles.length !== allowedCount) {
      toast.error(`Please select exactly ${allowedCount} profile(s) to keep`);
      return;
    }

    setConfirming(true);
    try {
      await onConfirm(selectedProfiles);
      onClose();
    } catch (error) {
      toast.error("Failed to update profiles");
    } finally {
      setConfirming(false);
    }
  };

  if (!isOpen) return null;

  const profilesToDeactivate = profiles.filter(p => !selectedProfiles.includes(p.id));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-viraly-card border border-white/10 rounded-2xl p-6 max-w-lg w-full shadow-2xl"
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-full bg-yellow-500/20 flex items-center justify-center">
            <AlertTriangle className="w-6 h-6 text-yellow-400" />
          </div>
          <div>
            <h3 className="font-heading text-lg font-bold">Select Profiles to Keep</h3>
            <p className="text-sm text-viraly-muted">
              Your plan allows {allowedCount} profile(s). Select which to keep active.
            </p>
          </div>
        </div>

        <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-3 mb-4">
          <p className="text-sm text-yellow-400">
            Profiles not selected will be <strong>deactivated</strong>. Their data will be preserved but you won't be able to create content for them until you upgrade.
          </p>
        </div>

        <div className="space-y-2 max-h-64 overflow-y-auto mb-4">
          {profiles.map((profile) => (
            <div
              key={profile.id}
              onClick={() => toggleProfile(profile.id)}
              className={`p-3 rounded-xl border cursor-pointer transition-all ${
                selectedProfiles.includes(profile.id)
                  ? 'border-viraly-orange bg-viraly-orange/10'
                  : 'border-white/10 bg-white/5 hover:border-white/20'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {profile.headshot_url ? (
                    <img src={profile.headshot_url} alt="" className="w-10 h-10 rounded-full object-cover" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-viraly-gradient flex items-center justify-center">
                      <span className="text-sm font-bold">{profile.name?.charAt(0)}</span>
                    </div>
                  )}
                  <div>
                    <p className="font-medium text-sm flex items-center gap-2">
                      {profile.name}
                      {profile.is_default && (
                        <span className="px-1.5 py-0.5 rounded text-[10px] bg-viraly-orange/20 text-viraly-orange">Default</span>
                      )}
                    </p>
                    <p className="text-xs text-viraly-muted">{profile.niche || 'No category'}</p>
                  </div>
                </div>
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                  selectedProfiles.includes(profile.id)
                    ? 'border-viraly-orange bg-viraly-orange'
                    : 'border-white/30'
                }`}>
                  {selectedProfiles.includes(profile.id) && (
                    <Check className="w-3 h-3 text-white" />
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between text-sm text-viraly-muted mb-4">
          <span>Selected: {selectedProfiles.length}/{allowedCount}</span>
          {profilesToDeactivate.length > 0 && (
            <span className="text-yellow-400">
              {profilesToDeactivate.length} profile(s) will be deactivated
            </span>
          )}
        </div>

        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={onClose}
            className="flex-1"
            disabled={confirming}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={selectedProfiles.length !== allowedCount || confirming}
            className="flex-1 bg-viraly-orange hover:bg-viraly-orange/90"
          >
            {confirming ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              `Confirm Selection`
            )}
          </Button>
        </div>
      </motion.div>
    </div>
  );
}

// Loyalty Section Component
function LoyaltySection({ user }) {
  const navigate = useNavigate();
  const totalSpent = user?.total_spent || 0;
  const usedTiers = user?.used_loyalty_tiers || [];
  
  // Find current tier
  const currentTierIndex = LOYALTY_TIERS.findIndex((tier, idx) => {
    const nextTier = LOYALTY_TIERS[idx + 1];
    return totalSpent >= tier.threshold && (!nextTier || totalSpent < nextTier.threshold);
  });
  
  const currentTier = currentTierIndex >= 0 ? LOYALTY_TIERS[currentTierIndex] : null;
  const nextTier = currentTierIndex < LOYALTY_TIERS.length - 1 ? LOYALTY_TIERS[currentTierIndex + 1] : null;
  
  // Calculate progress to next tier
  const progressToNext = nextTier 
    ? Math.min(100, ((totalSpent - (currentTier?.threshold || 0)) / (nextTier.threshold - (currentTier?.threshold || 0))) * 100)
    : 100;
  
  // Check if user has unused discount for next tier
  const nextAvailableDiscount = LOYALTY_TIERS.find(tier => 
    totalSpent >= tier.threshold && !usedTiers.includes(tier.name.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Sum Total Note */}
      <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
        <p className="text-xs text-blue-400 flex items-center gap-2">
          <Info className="w-4 h-4 flex-shrink-0" />
          <span>Loyalty rewards are calculated from your <strong>total spending across all profiles</strong>. Your tier and discounts apply to your entire account.</span>
        </p>
      </div>
      
      {/* Current Status */}
      <div className="grid grid-cols-2 gap-4">
        <div className="p-4 rounded-xl bg-viraly-dark border border-white/10">
          <p className="text-sm text-viraly-muted mb-1">Total Spent (All Profiles)</p>
          <p className="text-2xl font-bold text-viraly-orange">${totalSpent.toFixed(2)}</p>
        </div>
        <div className="p-4 rounded-xl bg-viraly-dark border border-white/10">
          <p className="text-sm text-viraly-muted mb-1">Current Tier</p>
          <p className={`text-2xl font-bold ${currentTier ? `text-${currentTier.color}-400` : 'text-white'}`}>
            {currentTier?.name || "Standard"}
          </p>
        </div>
      </div>

      {/* Available Discount Banner */}
      {nextAvailableDiscount && (
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="p-4 rounded-xl bg-viraly-gradient border border-viraly-orange/30"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Award className="w-8 h-8 text-white" />
              <div>
                <p className="font-bold text-white">You have an unused {nextAvailableDiscount.discount}% discount!</p>
                <p className="text-sm text-white/80">
                  {nextAvailableDiscount.name} tier reward - use it on your next purchase
                </p>
              </div>
            </div>
            <Button
              onClick={() => navigate('/pricing')}
              className="bg-white text-viraly-orange hover:bg-white/90"
            >
              Use Now
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </motion.div>
      )}

      {/* Progress to Next Tier */}
      {nextTier && (
        <div className="p-4 rounded-xl bg-white/5">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm text-viraly-muted">Progress to {nextTier.name}</span>
            <span className="text-sm font-medium">
              ${(nextTier.threshold - totalSpent).toFixed(2)} more to go
            </span>
          </div>
          <div className="w-full bg-white/10 rounded-full h-3">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${progressToNext}%` }}
              transition={{ duration: 1, ease: "easeOut" }}
              className="h-3 rounded-full"
              style={{ background: `linear-gradient(135deg, #FF6B35, #FF8C61)` }}
            />
          </div>
          <p className="text-xs text-viraly-muted mt-2">
            Reach {nextTier.name} to unlock a one-time <strong>{nextTier.discount}% discount</strong>
          </p>
        </div>
      )}

      {/* Tier Overview */}
      <div>
        <h3 className="text-sm font-medium mb-3">Loyalty Tiers</h3>
        <div className="grid grid-cols-5 gap-2">
          {LOYALTY_TIERS.map((tier) => {
            const isAchieved = totalSpent >= tier.threshold;
            const isUsed = usedTiers.includes(tier.name.toLowerCase());
            const isCurrent = currentTier?.name === tier.name;
            
            return (
              <div 
                key={tier.name}
                className={`p-3 rounded-xl text-center transition-all ${
                  isCurrent 
                    ? 'bg-viraly-orange/20 border-2 border-viraly-orange' 
                    : isAchieved 
                      ? 'bg-white/10 border border-white/20' 
                      : 'bg-white/5 border border-white/5 opacity-50'
                }`}
              >
                <p className={`font-bold text-sm ${isAchieved ? 'text-white' : 'text-viraly-muted'}`}>
                  {tier.name}
                </p>
                <p className="text-lg font-bold text-viraly-orange">{tier.discount}%</p>
                <p className="text-xs text-viraly-muted">${tier.threshold}+</p>
                {isAchieved && (
                  <span className={`text-[10px] mt-1 block ${isUsed ? 'text-viraly-muted' : 'text-viraly-success'}`}>
                    {isUsed ? 'Used' : 'Available'}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Encourage More Purchases */}
      <div className="p-4 rounded-xl bg-white/5 border border-white/10">
        <div className="flex items-start gap-3">
          <TrendingUp className="w-5 h-5 text-viraly-orange flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium">Keep Growing!</p>
            <p className="text-xs text-viraly-muted mt-1">
              The more you invest in your content, the more you save. Each tier unlocks a one-time discount 
              that you can use whenever you're ready. Your discounts never expire!
            </p>
            <Button 
              onClick={() => navigate('/pricing')}
              className="btn-secondary mt-3 text-sm py-2"
            >
              Browse Plans & Bundles
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function SettingsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const initialTab = searchParams.get("tab") || "profile";
  
  const { user, updateUser } = useAuth();
  const { switchProfile: contextSwitchProfile, refreshProfiles, activeProfile } = useProfile();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(initialTab);
  const [socialStatus, setSocialStatus] = useState(null);
  const [connectingPlatform, setConnectingPlatform] = useState(null);
  const [brandUrl, setBrandUrl] = useState("");
  const [learningBrand, setLearningBrand] = useState(false);
  
  // Connection modal states
  const [connectionSuccessModal, setConnectionSuccessModal] = useState({ isOpen: false, platform: null, accountName: "", accountHandle: "" });
  const [disconnectModal, setDisconnectModal] = useState({ isOpen: false, platform: null, accountId: null, accountName: "" });
  
  // Detect connection success from OAuth callback redirect
  // Use searchParams.toString() as dependency to ensure useEffect runs when URL params change
  const searchParamsString = searchParams.toString();
  useEffect(() => {
    const connected = searchParams.get("connected");
    const platform = searchParams.get("platform");
    const account = searchParams.get("account");
    
    if (connected === "true" && platform) {
      // Show success modal
      setConnectionSuccessModal({
        isOpen: true,
        platform: platform,
        accountName: account || platform,
        accountHandle: ""
      });
      
      // Clean up URL params without triggering navigation
      const newParams = new URLSearchParams(searchParams);
      newParams.delete("connected");
      newParams.delete("platform");
      newParams.delete("account");
      setSearchParams(newParams, { replace: true });
    }
  }, [searchParamsString, setSearchParams]);
  
  // Headshot and Logo upload states
  const [uploadingHeadshot, setUploadingHeadshot] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  
  // Profile edit mode
  const [profileEditMode, setProfileEditMode] = useState(false);
  const [originalProfile, setOriginalProfile] = useState(null);
  
  const [profile, setProfile] = useState({
    name: user?.name || "",
    brand_voice: user?.brand_voice || "",
    target_audience: user?.target_audience || "",
    brand_keywords: user?.brand_keywords || "",
    content_avoid: user?.content_avoid || ""
  });
  
  // Timezone and currency state
  const [selectedTimezone, setSelectedTimezone] = useState(user?.timezone || "");
  const [selectedCurrency, setSelectedCurrency] = useState(user?.currency || "USD");
  
  // Auto-Posting Permissions State (TikTok & Pinterest)
  const [tiktokAutoSettings, setTiktokAutoSettings] = useState({
    default_privacy_level: "",
    allow_comment: true,
    allow_duet: false,
    allow_stitch: false,
    disclosure_enabled: false,
    is_your_brand: false,
    is_branded_content: false,
    auto_posting_consent: false
  });
  const [pinterestAutoSettings, setPinterestAutoSettings] = useState({
    default_board_id: "",
    default_board_name: "",
    auto_posting_consent: false
  });
  const [pinterestBoards, setPinterestBoards] = useState([]);
  const [loadingAutoSettings, setLoadingAutoSettings] = useState(false);
  const [savingAutoSettings, setSavingAutoSettings] = useState(false);
  
  // Active plans state (Multi-Plan Credit System)
  const [activePlans, setActivePlans] = useState([]);
  const [creditSummary, setCreditSummary] = useState(null);
  const [loadingPlans, setLoadingPlans] = useState(false);
  
  // Brand profiles state (Multi-Profile System)
  const [brandProfiles, setBrandProfiles] = useState([]);
  const [profileLimit, setProfileLimit] = useState(1);
  const [loadingProfiles, setLoadingProfiles] = useState(false);
  const [createProfileModal, setCreateProfileModal] = useState(false);
  const [createProfileStep, setCreateProfileStep] = useState(1); // Multi-step wizard: 1=Basic Info, 2=Brand Learning, 3=Headshot/Logo, 4=Social Accounts
  const [deleteProfileId, setDeleteProfileId] = useState(null);
  const [deleteProfileName, setDeleteProfileName] = useState('');
  const [showDowngradeModal, setShowDowngradeModal] = useState(false);
  const [newProfileData, setNewProfileData] = useState({
    name: '',
    niche: '',
    url: '',
    // Step 2: Brand learning results
    brandLearningResult: null,
    brandLearningUrl: '',
    brandLearningLoading: false,
    // Step 3: Headshot/Logo
    headshotFile: null,
    headshotPreview: null,
    logoFile: null,
    logoPreview: null,
    brandType: 'personal', // 'personal' or 'business'
    // Step 4: Social accounts pre-population
    detectedSocialUrl: null, // URL detected from step 1
    socialAccountsConfirmed: false
  });
  
  // Track current profile for Brand & Social tabs (profile-aware)
  const [profileBrandData, setProfileBrandData] = useState({
    brand_voice: '',
    target_audience: '',
    brand_keywords: '',
    content_avoid: '',
    sender_email: '' // Email for sending content from this profile
  });
  const [profileSocialStatus, setProfileSocialStatus] = useState(null);
  const [loadingProfileData, setLoadingProfileData] = useState(false);

  // Fetch brand profiles when profile tab is active
  useEffect(() => {
    if (activeTab === "profile" && user) {
      fetchBrandProfiles();
    }
  }, [activeTab, user]);
  
  // Check if profiles exceed limit (for downgrade scenario)
  useEffect(() => {
    const activeProfiles = brandProfiles.filter(p => p.status !== 'inactive');
    if (activeProfiles.length > profileLimit && profileLimit > 0 && brandProfiles.length > 0) {
      setShowDowngradeModal(true);
    }
  }, [brandProfiles, profileLimit]);
  
  // Fetch profile-specific data for Brand tab (profile-aware)
  useEffect(() => {
    if (activeTab === "brand" && activeProfile?.id) {
      fetchProfileBrandData();
    }
  }, [activeTab, activeProfile?.id]);
  
  // Fetch profile-specific social accounts for Social tab (profile-aware)
  useEffect(() => {
    if (activeTab === "social" && activeProfile?.id) {
      fetchProfileSocialStatus();
    }
  }, [activeTab, activeProfile?.id]);
  
  // Sync brandUrl with activeProfile when it changes
  useEffect(() => {
    if (activeProfile) {
      setBrandUrl(activeProfile.brand_learning?.source_url || '');
      // Also update profile brand data when activeProfile changes
      setProfileBrandData({
        brand_voice: activeProfile.brand_voice || activeProfile.brand_learning?.brand_voice || '',
        target_audience: activeProfile.target_audience || activeProfile.brand_learning?.target_audience || '',
        brand_keywords: activeProfile.brand_keywords || activeProfile.brand_learning?.brand_keywords || '',
        content_avoid: activeProfile.content_avoid || ''
      });
    }
  }, [activeProfile]);
  
  const fetchProfileBrandData = async () => {
    if (!activeProfile?.id) return;
    setLoadingProfileData(true);
    try {
      const res = await apiClient.get(`/users/profiles/${activeProfile.id}`);
      const profileData = res.data;
      setProfileBrandData({
        brand_voice: profileData.brand_voice || profileData.brand_learning?.brand_voice || '',
        target_audience: profileData.target_audience || profileData.brand_learning?.target_audience || '',
        brand_keywords: profileData.brand_keywords || profileData.brand_learning?.brand_keywords || '',
        content_avoid: profileData.content_avoid || '',
        sender_email: profileData.sender_email || ''
      });
      setBrandUrl(profileData.brand_learning?.source_url || profileData.brand_url || '');
    } catch (err) {
      console.error("Failed to fetch profile brand data:", err);
    } finally {
      setLoadingProfileData(false);
    }
  };
  
  const fetchProfileSocialStatus = async () => {
    if (!activeProfile?.id) return;
    try {
      // Fetch social status for the active profile
      const res = await apiClient.get("/social/oauth/status");
      // Filter to show only accounts connected to this profile
      const profileSocials = activeProfile.social_accounts || {};
      const status = res.data;
      
      // Merge profile-specific social accounts with general status
      setProfileSocialStatus({
        facebook: {
          connected: profileSocials.facebook?.connected || status?.facebook?.connected,
          accounts: profileSocials.facebook?.accounts || status?.facebook?.accounts || []
        },
        instagram: {
          connected: profileSocials.instagram?.connected || status?.instagram?.connected,
          accounts: profileSocials.instagram?.accounts || status?.instagram?.accounts || []
        },
        tiktok: {
          connected: profileSocials.tiktok?.connected || status?.tiktok?.connected,
          accounts: profileSocials.tiktok?.accounts || status?.tiktok?.accounts || []
        },
        pinterest: {
          connected: profileSocials.pinterest?.connected || status?.pinterest?.connected,
          accounts: profileSocials.pinterest?.accounts || status?.pinterest?.accounts || []
        }
      });
    } catch (error) {
      console.error("Failed to fetch profile social status:", error);
    }
  };
  
  const fetchBrandProfiles = async () => {
    setLoadingProfiles(true);
    try {
      const res = await apiClient.get("/users/profiles");
      setBrandProfiles(res.data.profiles || []);
      setProfileLimit(res.data.profile_limit || 1);
    } catch (err) {
      console.error("Failed to fetch profiles:", err);
    } finally {
      setLoadingProfiles(false);
    }
  };
  
  const switchBrandProfile = async (profileId) => {
    try {
      // Use ProfileContext's switch to ensure header updates immediately
      await contextSwitchProfile(profileId);
      // Also refresh local state
      fetchBrandProfiles();
    } catch (err) {
      toast.error("Failed to switch profile");
    }
  };
  
  const handleCreateProfile = async () => {
    if (!newProfileData.name) {
      toast.error("Profile name is required");
      return;
    }
    try {
      const res = await apiClient.post("/users/profiles", {
        name: newProfileData.name,
        niche: newProfileData.niche,
        brand_url: newProfileData.url  // Send URL for brand learning
      });
      
      const newProfile = res.data.profile;
      toast.success("Profile created!");
      
      // If URL was provided, move to Step 2 (brand learning)
      if (newProfileData.url) {
        setNewProfileData(prev => ({ 
          ...prev, 
          profileId: newProfile.id,
          brandLearningUrl: newProfileData.url 
        }));
        setCreateProfileStep(2);
        // Auto-trigger brand learning
        handleWizardBrandLearning(newProfile.id, newProfileData.url);
      } else {
        // No URL, skip to Step 3 (headshot/logo)
        setNewProfileData(prev => ({ ...prev, profileId: newProfile.id }));
        setCreateProfileStep(3);
      }
      
      fetchBrandProfiles();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create profile");
    }
  };
  
  // Handle brand learning in wizard Step 2
  const handleWizardBrandLearning = async (profileId, url) => {
    if (!url) return;
    
    setNewProfileData(prev => ({ ...prev, brandLearningLoading: true }));
    try {
      const res = await apiClient.post("/brand/learn-from-url", { 
        url,
        profile_id: profileId  // Associate with new profile
      });
      
      if (res.data.brand_voice || res.data.business_name) {
        setNewProfileData(prev => ({
          ...prev,
          brandLearningResult: res.data,
          brandLearningLoading: false
        }));
        
        // Detect if URL is a social media URL for Step 4
        const socialUrl = detectSocialPlatformFromUrl(url);
        if (socialUrl) {
          setNewProfileData(prev => ({ ...prev, detectedSocialUrl: socialUrl }));
        }
        
        toast.success("Brand analyzed successfully!");
      } else {
        toast.error("Could not analyze brand from URL");
        setNewProfileData(prev => ({ ...prev, brandLearningLoading: false }));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to analyze brand");
      setNewProfileData(prev => ({ ...prev, brandLearningLoading: false }));
    }
  };
  
  // Helper to detect social platform from URL
  const detectSocialPlatformFromUrl = (url) => {
    if (!url) return null;
    const lowerUrl = url.toLowerCase();
    if (lowerUrl.includes('facebook.com') || lowerUrl.includes('fb.com')) {
      return { platform: 'facebook', url };
    }
    if (lowerUrl.includes('instagram.com')) {
      return { platform: 'instagram', url };
    }
    if (lowerUrl.includes('tiktok.com')) {
      return { platform: 'tiktok', url };
    }
    if (lowerUrl.includes('pinterest.com')) {
      return { platform: 'pinterest', url };
    }
    if (lowerUrl.includes('linkedin.com')) {
      return { platform: 'linkedin', url };
    }
    return null;
  };
  
  // Confirm brand learning and save to profile
  const handleConfirmBrandLearning = async () => {
    if (!newProfileData.profileId || !newProfileData.brandLearningResult) return;
    
    try {
      await apiClient.put(`/users/profiles/${newProfileData.profileId}`, {
        brand_voice: newProfileData.brandLearningResult.brand_voice,
        target_audience: newProfileData.brandLearningResult.target_audience,
        brand_keywords: newProfileData.brandLearningResult.brand_keywords,
        brand_learning: {
          ...newProfileData.brandLearningResult,
          source_url: newProfileData.brandLearningUrl,
          learned_at: new Date().toISOString()
        }
      });
      toast.success("Brand learning saved!");
      setCreateProfileStep(3);
    } catch (err) {
      toast.error("Failed to save brand learning");
    }
  };
  
  // Handle headshot upload in wizard Step 3
  const handleWizardHeadshotUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file");
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File too large. Max 5MB");
      return;
    }
    
    // Show preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setNewProfileData(prev => ({ 
        ...prev, 
        headshotFile: file,
        headshotPreview: e.target.result 
      }));
    };
    reader.readAsDataURL(file);
  };
  
  // Handle logo upload in wizard Step 3
  const handleWizardLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file");
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File too large. Max 5MB");
      return;
    }
    
    // Show preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setNewProfileData(prev => ({ 
        ...prev, 
        logoFile: file,
        logoPreview: e.target.result 
      }));
    };
    reader.readAsDataURL(file);
  };
  
  // Save headshot/logo and move to Step 4
  const handleSaveHeadshotLogo = async () => {
    const profileId = newProfileData.profileId;
    if (!profileId) return;
    
    try {
      // Upload headshot if provided
      if (newProfileData.headshotFile) {
        const formData = new FormData();
        formData.append("file", newProfileData.headshotFile);
        await apiClient.post(`/users/profiles/${profileId}/headshot`, formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
      }
      
      // Upload logo if provided
      if (newProfileData.logoFile) {
        const formData = new FormData();
        formData.append("file", newProfileData.logoFile);
        await apiClient.post(`/users/profiles/${profileId}/logo`, formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
      }
      
      // Save brand type preference
      await apiClient.put(`/users/profiles/${profileId}`, {
        brand_type: newProfileData.brandType
      });
      
      toast.success("Images saved!");
      setCreateProfileStep(4);
      fetchBrandProfiles();
    } catch (err) {
      toast.error("Failed to save images");
    }
  };
  
  // Complete wizard and close modal
  const handleCompleteWizard = async () => {
    // ENFORCE: Validate all wizard steps are complete
    
    // Check 1: Profile must exist
    if (!newProfileData.profileId) {
      toast.error("Please complete Step 1: Create Profile");
      setCreateProfileStep(1);
      return;
    }
    
    // Check 2: Brand learning must be confirmed
    if (!newProfileData.brandLearningResult) {
      toast.error("Please complete Step 2: Brand Learning is required for personalized content");
      setCreateProfileStep(2);
      return;
    }
    
    // Check 3: Headshot OR Logo must be uploaded (at least one)
    if (!newProfileData.headshotFile && !newProfileData.logoFile && !newProfileData.headshotPreview && !newProfileData.logoPreview) {
      toast.error("Please complete Step 3: Upload at least a profile image or logo");
      setCreateProfileStep(3);
      return;
    }
    
    // Check 4: At least one social account must be connected
    // Get current social status for this profile
    const profileSocials = profileSocialStatus || socialStatus;
    const hasConnectedAccount = profileSocials && (
      profileSocials.facebook?.connected ||
      profileSocials.instagram?.connected ||
      profileSocials.tiktok?.connected ||
      profileSocials.pinterest?.connected
    );
    
    if (!hasConnectedAccount) {
      toast.error("Please connect at least one social media account to publish content");
      setCreateProfileStep(4);
      return;
    }
    
    // All validations passed - complete the wizard
    await fetchBrandProfiles();
    await refreshProfiles();
    setCreateProfileModal(false);
    resetWizardState();
    toast.success("Profile setup complete! 🎉 Your profile is ready to create amazing content.");
  };
  
  // Reset wizard state
  const resetWizardState = () => {
    setCreateProfileStep(1);
    setNewProfileData({
      name: '',
      niche: '',
      url: '',
      profileId: null,
      brandLearningResult: null,
      brandLearningUrl: '',
      brandLearningLoading: false,
      headshotFile: null,
      headshotPreview: null,
      logoFile: null,
      logoPreview: null,
      brandType: 'personal',
      detectedSocialUrl: null,
      socialAccountsConfirmed: false
    });
  };
  
  // Handle downgrade profile selection (deactivate unselected profiles)
  const handleDowngradeProfileSelection = async (selectedProfileIds) => {
    const profilesToDeactivate = brandProfiles.filter(p => !selectedProfileIds.includes(p.id));
    
    try {
      // Deactivate profiles not selected
      for (const profile of profilesToDeactivate) {
        await apiClient.put(`/users/profiles/${profile.id}/status`, { status: 'inactive' });
      }
      
      await fetchBrandProfiles();
      toast.success(`${profilesToDeactivate.length} profile(s) deactivated. You can reactivate them after upgrading.`);
    } catch (error) {
      throw error;
    }
  };
  
  const handleDeleteProfile = async () => {
    if (!deleteProfileId) return;
    try {
      await apiClient.delete(`/users/profiles/${deleteProfileId}`);
      toast.success("Profile deleted successfully");
      setDeleteProfileId(null);
      setDeleteProfileName('');
      fetchBrandProfiles();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete profile");
    }
  };

  // Fetch active plans when billing tab is active
  useEffect(() => {
    if (activeTab === "billing" && user) {
      fetchActivePlans();
    }
  }, [activeTab, user]);

  const fetchActivePlans = async () => {
    setLoadingPlans(true);
    try {
      const [plansRes, summaryRes] = await Promise.all([
        apiClient.get("/users/plans"),
        apiClient.get("/users/credit-summary")
      ]);
      setActivePlans(plansRes.data.plans || []);
      setCreditSummary(summaryRes.data);
    } catch (err) {
      console.error("Failed to fetch plans:", err);
    } finally {
      setLoadingPlans(false);
    }
  };

  // Update profile when user data changes (e.g., after brand learning or page refresh)
  useEffect(() => {
    if (user) {
      setProfile({
        name: user.name || "",
        brand_voice: user.brand_voice || "",
        target_audience: user.target_audience || "",
        brand_keywords: user.brand_keywords || "",
        content_avoid: user.content_avoid || ""
      });
      setSelectedTimezone(user.timezone || "");
      setSelectedCurrency(user.currency || "USD");
    }
  }, [user?.brand_voice, user?.target_audience, user?.brand_keywords, user?.timezone, user?.currency]);

  // Update brandUrl when user data changes
  useEffect(() => {
    if (user?.brand_url) {
      setBrandUrl(user.brand_url);
    }
  }, [user?.brand_url]);

  // Handle headshot upload
  const handleHeadshotUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file");
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File too large. Max 5MB");
      return;
    }
    
    setUploadingHeadshot(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      
      await apiClient.post("/users/headshot", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      // Refresh user data to get new headshot_url
      const meRes = await apiClient.get("/auth/me");
      updateUser(meRes.data);
      
      toast.success("Headshot uploaded! This will appear on your branded posts.");
    } catch (error) {
      toast.error("Failed to upload headshot");
    } finally {
      setUploadingHeadshot(false);
    }
  };
  
  // Handle logo upload
  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file");
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File too large. Max 5MB");
      return;
    }
    
    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      
      await apiClient.post("/users/logo", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      // Refresh user data
      const meRes = await apiClient.get("/auth/me");
      updateUser(meRes.data);
      
      toast.success("Logo uploaded! This will appear on company-branded posts.");
    } catch (error) {
      toast.error("Failed to upload logo");
    } finally {
      setUploadingLogo(false);
    }
  };

  useEffect(() => {
    if (activeTab === "social") {
      fetchSocialStatus();
    }
  }, [activeTab]);

  const fetchSocialStatus = async () => {
    try {
      const res = await apiClient.get("/social/oauth/status");
      setSocialStatus(res.data);
    } catch (error) {
      console.error("Failed to fetch social status:", error);
    }
  };

  // Fetch auto-posting settings for TikTok and Pinterest
  const fetchAutoPostingSettings = async () => {
    setLoadingAutoSettings(true);
    try {
      // Fetch TikTok settings
      const ttRes = await apiClient.get("/social/tiktok/auto-posting-settings");
      if (ttRes.data) {
        setTiktokAutoSettings({
          default_privacy_level: ttRes.data.default_privacy_level || "",
          allow_comment: ttRes.data.allow_comment ?? true,
          allow_duet: ttRes.data.allow_duet ?? false,
          allow_stitch: ttRes.data.allow_stitch ?? false,
          disclosure_enabled: ttRes.data.disclosure_enabled ?? false,
          is_your_brand: ttRes.data.is_your_brand ?? false,
          is_branded_content: ttRes.data.is_branded_content ?? false,
          auto_posting_consent: ttRes.data.auto_posting_consent ?? false
        });
      }
      
      // Fetch Pinterest settings
      const pinRes = await apiClient.get("/social/pinterest/auto-posting-settings");
      if (pinRes.data) {
        setPinterestAutoSettings({
          default_board_id: pinRes.data.default_board_id || "",
          default_board_name: pinRes.data.default_board_name || "",
          auto_posting_consent: pinRes.data.auto_posting_consent ?? false
        });
      }
      
      // Fetch Pinterest boards if connected
      try {
        const boardsRes = await apiClient.get("/social/pinterest/boards");
        if (boardsRes.data?.boards) {
          setPinterestBoards(boardsRes.data.boards);
        }
      } catch (e) {
        // Pinterest not connected - that's okay
      }
    } catch (error) {
      console.error("Failed to fetch auto-posting settings:", error);
    } finally {
      setLoadingAutoSettings(false);
    }
  };

  const saveTiktokAutoSettings = async () => {
    if (tiktokAutoSettings.auto_posting_consent && !tiktokAutoSettings.default_privacy_level) {
      toast.error("Please select a default privacy level before enabling auto-posting");
      return;
    }
    
    setSavingAutoSettings(true);
    try {
      await apiClient.put("/social/tiktok/auto-posting-settings", tiktokAutoSettings);
      toast.success("TikTok auto-posting settings saved!");
    } catch (error) {
      toast.error("Failed to save TikTok settings");
    } finally {
      setSavingAutoSettings(false);
    }
  };

  const savePinterestAutoSettings = async () => {
    setSavingAutoSettings(true);
    try {
      await apiClient.put("/social/pinterest/auto-posting-settings", pinterestAutoSettings);
      toast.success("Pinterest auto-posting settings saved!");
    } catch (error) {
      toast.error("Failed to save Pinterest settings");
    } finally {
      setSavingAutoSettings(false);
    }
  };

  useEffect(() => {
    if (activeTab === "notifications") {
      fetchAutoPostingSettings();
    }
  }, [activeTab]);

  const handleLearnBrand = async () => {
    if (!brandUrl) return;
    if (!activeProfile?.id) {
      toast.error("Please select a profile first");
      return;
    }
    
    setLearningBrand(true);
    try {
      const res = await apiClient.post("/brand/learn-from-url", { 
        url: brandUrl,
        profile_id: activeProfile.id  // Associate with active profile
      });
      
      if (res.data.brand_voice) {
        // Update the profile-specific brand data
        const newBrandData = {
          brand_voice: res.data.brand_voice || profileBrandData.brand_voice,
          target_audience: res.data.target_audience || profileBrandData.target_audience,
          brand_keywords: res.data.brand_keywords || profileBrandData.brand_keywords,
          content_avoid: res.data.content_avoid || profileBrandData.content_avoid
        };
        setProfileBrandData(newBrandData);
        
        // Auto-save the brand profile for the active profile
        try {
          await apiClient.put(`/users/profiles/${activeProfile.id}`, {
            ...newBrandData,
            brand_url: brandUrl,
            brand_learning: {
              ...res.data,
              source_url: brandUrl,
              learned_at: new Date().toISOString()
            }
          });
          await refreshProfiles();
          toast.success("Brand learned and saved!", {
            description: `Brand profile for ${activeProfile.name} has been updated`
          });
        } catch (saveError) {
          toast.success("Brand learned!", {
            description: "Click 'Save Brand Settings' to save changes"
          });
        }
      } else {
        toast.error("Could not analyze brand from URL");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to analyze website");
    } finally {
      setLearningBrand(false);
    }
  };

  // OAuth Connect Modal State
  const [oauthModal, setOauthModal] = useState({
    isOpen: false,
    platform: null,
    connectUrl: null,
    loading: false
  });

  const handleConnectPlatform = async (platform) => {
    setConnectingPlatform(platform);
    setOauthModal(prev => ({ ...prev, loading: true, platform }));
    
    try {
      // Use bundle.social for social connections (branded OAuth flow)
      const res = await apiClient.post("/social/bundle-social/connect", { platforms: [platform] });
      
      if (res.data.success && res.data.connect_url) {
        // Show beautiful branded OAuth modal
        setOauthModal({
          isOpen: true,
          platform: platform,
          connectUrl: res.data.connect_url,
          loading: false
        });
      } else {
        // Fallback to native OAuth if bundle.social not configured
        const nativeRes = await apiClient.get(`/social/oauth/${platform}/url`);
        if (nativeRes.data.url) {
          window.location.href = nativeRes.data.url;
        } else {
          toast.info(`Connect your ${platform} account`, {
            description: "OAuth integration being configured. Please try again shortly.",
            duration: 5000
          });
        }
      }
    } catch (error) {
      console.error("Connect error:", error);
      if (error.response?.status === 503) {
        toast.error(`${platform} integration not available`, {
          description: "We're working on enabling this platform. Please try again later."
        });
      } else {
        toast.error(`Failed to connect ${platform}`, {
          description: "Please try again or contact support."
        });
      }
    } finally {
      setConnectingPlatform(null);
      setOauthModal(prev => ({ ...prev, loading: false }));
    }
  };

  // Handle OAuth popup flow
  const openOAuthPopup = () => {
    if (!oauthModal.connectUrl) return;
    
    // Open in popup window for better UX
    const width = 600;
    const height = 700;
    const left = window.screen.width / 2 - width / 2;
    const top = window.screen.height / 2 - height / 2;
    
    const popup = window.open(
      oauthModal.connectUrl,
      'viraly_oauth',
      `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes,resizable=yes`
    );
    
    // Close modal
    setOauthModal({ isOpen: false, platform: null, connectUrl: null, loading: false });
    
    // Poll for popup close
    const checkClosed = setInterval(() => {
      if (popup?.closed) {
        clearInterval(checkClosed);
        // Refresh social status
        fetchSocialStatus();
        toast.success(`Checking ${oauthModal.platform} connection...`);
      }
    }, 500);
  };

  const handleDisconnect = async (platform, accountId, accountName = "") => {
    // Show confirmation modal instead of direct delete
    setDisconnectModal({
      isOpen: true,
      platform: platform,
      accountId: accountId,
      accountName: accountName || `your ${platform} account`
    });
  };

  // Save profile-aware brand settings (for the active profile)
  const handleSaveProfileBrand = async () => {
    if (!activeProfile?.id) {
      toast.error("No active profile selected");
      return;
    }
    
    setLoading(true);
    try {
      await apiClient.put(`/users/profiles/${activeProfile.id}`, {
        brand_voice: profileBrandData.brand_voice,
        target_audience: profileBrandData.target_audience,
        brand_keywords: profileBrandData.brand_keywords,
        content_avoid: profileBrandData.content_avoid,
        brand_url: brandUrl,
        sender_email: profileBrandData.sender_email
      });
      
      // Refresh profiles to update context
      await refreshProfiles();
      
      toast.success(`Brand settings saved for ${activeProfile.name}!`, {
        description: "Your profile settings have been updated"
      });
    } catch (error) {
      console.error("Save error:", error);
      toast.error(error.response?.data?.detail || "Failed to update brand settings");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    setLoading(true);
    // Include brand_url, timezone, and currency in the save data
    const saveData = {
      ...profile,
      brand_url: brandUrl,
      timezone: selectedTimezone || undefined,
      currency: selectedCurrency || undefined
    };
    console.log("Saving profile:", saveData);
    try {
      const res = await apiClient.put("/users/profile", saveData);
      console.log("Save response:", res.data);
      updateUser(res.data);
      toast.success("Profile saved successfully!", {
        description: "Your settings have been updated"
      });
    } catch (error) {
      console.error("Save error:", error);
      toast.error(error.response?.data?.detail || "Failed to update profile");
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="p-6 md:p-8 max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-bold mb-1">Settings</h1>
          <p className="text-viraly-muted">Manage your account and preferences</p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-viraly-card border border-white/10 p-1 mb-6">
            <TabsTrigger 
              value="profile" 
              className="data-[state=active]:bg-viraly-gradient data-[state=active]:text-white"
              data-testid="tab-profile"
            >
              <User className="w-4 h-4 mr-2" />
              Profile
            </TabsTrigger>
            <TabsTrigger 
              value="brand" 
              className="data-[state=active]:bg-viraly-gradient data-[state=active]:text-white"
              data-testid="tab-brand"
            >
              <Palette className="w-4 h-4 mr-2" />
              Brand
            </TabsTrigger>
            <TabsTrigger 
              value="billing" 
              className="data-[state=active]:bg-viraly-gradient data-[state=active]:text-white"
              data-testid="tab-billing"
            >
              <CreditCard className="w-4 h-4 mr-2" />
              Billing
            </TabsTrigger>
            <TabsTrigger 
              value="notifications" 
              className="data-[state=active]:bg-viraly-gradient data-[state=active]:text-white"
              data-testid="tab-notifications"
            >
              <Bell className="w-4 h-4 mr-2" />
              Notifications & Permissions
            </TabsTrigger>
            <TabsTrigger 
              value="social" 
              className="data-[state=active]:bg-viraly-gradient data-[state=active]:text-white"
              data-testid="tab-social"
            >
              <Link2 className="w-4 h-4 mr-2" />
              Social
            </TabsTrigger>
          </TabsList>

          <TabsContent value="profile">
            {/* Brand Profiles Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="card-base p-6 mb-6"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-viraly-orange" />
                  <h2 className="font-heading text-lg font-bold">Brand Profiles</h2>
                  <span className="text-sm text-viraly-muted">
                    {brandProfiles.length} / {profileLimit}
                  </span>
                </div>
                {brandProfiles.length < profileLimit && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCreateProfileModal(true)}
                    className="border-viraly-orange/30 text-viraly-orange hover:bg-viraly-orange/10"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Profile
                  </Button>
                )}
              </div>
              
              {loadingProfiles ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin w-6 h-6 border-2 border-viraly-orange border-t-transparent rounded-full"></div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {brandProfiles.map((bp, idx) => {
                    // Calculate profile completion score
                    const completionItems = [
                      { done: !!bp.name, label: 'Name' },
                      { done: !!bp.niche, label: 'Niche' },
                      { done: !!bp.headshot_url || !!bp.logo_url, label: 'Image' },
                      { done: !!bp.brand_learning, label: 'Brand Learning' },
                      { done: bp.social_accounts && Object.values(bp.social_accounts).some(s => s?.connected), label: 'Social' }
                    ];
                    const completedCount = completionItems.filter(item => item.done).length;
                    const completionPercent = Math.round((completedCount / completionItems.length) * 100);
                    const missingItems = completionItems.filter(item => !item.done).map(item => item.label);
                    
                    return (
                    <div
                      key={bp.id || idx}
                      className={`p-4 rounded-xl border transition-all relative group ${
                        bp.is_default
                          ? 'bg-viraly-orange/10 border-viraly-orange/30'
                          : 'bg-white/5 border-white/10 hover:border-viraly-orange/30 cursor-pointer'
                      }`}
                      onClick={() => !bp.is_default && switchBrandProfile(bp.id)}
                    >
                      {/* Delete button - only show for non-active, non-primary profiles */}
                      {!bp.is_default && idx > 0 && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteProfileId(bp.id);
                            setDeleteProfileName(bp.name);
                          }}
                          className="absolute top-2 right-2 p-1.5 rounded-lg bg-red-500/10 text-red-400 opacity-0 group-hover:opacity-100 hover:bg-red-500/20 transition-all"
                          title="Delete profile"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                      <div className="flex items-start gap-3">
                        {bp.headshot_url ? (
                          <img
                            src={bp.headshot_url}
                            alt={bp.name}
                            className="w-12 h-12 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-12 h-12 rounded-full bg-viraly-gradient flex items-center justify-center">
                            <span className="text-lg font-bold text-white">
                              {bp.name?.charAt(0).toUpperCase()}
                            </span>
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h3 className="font-medium text-viraly-light truncate">{bp.name}</h3>
                            {bp.is_default && (
                              <span className="text-[10px] px-2 py-0.5 bg-viraly-orange/20 text-viraly-orange rounded-full uppercase">
                                Active
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-viraly-muted mt-0.5">
                            {bp.niche || bp.brand_learning?.business_category || 'No category set'}
                          </p>
                          
                          {/* Profile Completion Score */}
                          <div className="mt-2">
                            <div className="flex items-center justify-between text-xs mb-1">
                              <span className={`font-medium ${
                                completionPercent === 100 ? 'text-green-400' : 
                                completionPercent >= 60 ? 'text-yellow-400' : 'text-viraly-muted'
                              }`}>
                                {completionPercent === 100 ? '✓ Complete' : `${completionPercent}% Complete`}
                              </span>
                              {completionPercent < 100 && (
                                <span className="text-viraly-muted opacity-60">
                                  Missing: {missingItems.join(', ')}
                                </span>
                              )}
                            </div>
                            <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                              <div 
                                className={`h-full rounded-full transition-all ${
                                  completionPercent === 100 ? 'bg-green-500' : 
                                  completionPercent >= 60 ? 'bg-yellow-500' : 'bg-viraly-orange'
                                }`}
                                style={{ width: `${completionPercent}%` }}
                              />
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-3 mt-2 text-xs text-viraly-muted">
                            <span>{bp.posts_count || 0} posts</span>
                            <span>{bp.videos_count || 0} videos</span>
                            {bp.social_accounts && (
                              <span className="flex items-center gap-1">
                                {Object.values(bp.social_accounts).filter(s => s?.connected).length} connected
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    );
                  })}
                  
                  {/* Add profile slot indicator */}
                  {brandProfiles.length < profileLimit && (
                    <div
                      className="p-4 rounded-xl border border-dashed border-white/20 flex items-center justify-center text-viraly-muted hover:border-viraly-orange/30 hover:text-viraly-orange cursor-pointer transition-all"
                      onClick={() => setCreateProfileModal(true)}
                    >
                      <div className="text-center">
                        <Plus className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">{profileLimit - brandProfiles.length} more profile{profileLimit - brandProfiles.length > 1 ? 's' : ''} available</p>
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              <p className="text-xs text-viraly-muted mt-4">
                💡 Each profile can have its own brand voice, social accounts, and content. Click a profile to switch to it.
              </p>
            </motion.div>
            
            {/* User Profile Information */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="card-base p-6"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-heading text-xl font-bold">Profile Information</h2>
                {!profileEditMode && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setOriginalProfile({
                        name: profile.name,
                        timezone: selectedTimezone,
                        currency: selectedCurrency
                      });
                      setProfileEditMode(true);
                    }}
                    className="border-white/20 hover:bg-white/10"
                    data-testid="edit-profile-btn"
                  >
                    <Edit2 className="w-4 h-4 mr-2" />
                    Edit
                  </Button>
                )}
              </div>
              
              <div className="space-y-6">
                <div className="flex items-center gap-6">
                  <div className="w-20 h-20 rounded-full bg-viraly-gradient flex items-center justify-center">
                    <span className="text-3xl font-bold text-white">
                      {user?.name?.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <p className="font-bold text-lg">{user?.name}</p>
                    <p className="text-viraly-muted">{user?.email}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <p className="text-sm text-viraly-orange capitalize">{user?.plan} Plan</p>
                      {user?.loyalty_tier && (
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          user.loyalty_tier === 'platinum' ? 'bg-purple-500/20 text-purple-400' :
                          user.loyalty_tier === 'gold' ? 'bg-yellow-500/20 text-yellow-400' :
                          user.loyalty_tier === 'silver' ? 'bg-gray-400/20 text-gray-300' :
                          'bg-amber-500/20 text-amber-400'
                        }`}>
                          {user.loyalty_tier.charAt(0).toUpperCase() + user.loyalty_tier.slice(1)} Loyalty
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="name">Display Name</Label>
                    {profileEditMode ? (
                      <Input
                        id="name"
                        value={profile.name}
                        onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                        className="input-base mt-2"
                        data-testid="profile-name-input"
                      />
                    ) : (
                      <p className="mt-2 p-3 rounded-lg bg-white/5 text-viraly-light">{profile.name || "Not set"}</p>
                    )}
                  </div>
                  
                  <div>
                    <Label htmlFor="email">Email</Label>
                    <p className="mt-2 p-3 rounded-lg bg-white/5 text-viraly-muted">{user?.email || "Not set"}</p>
                  </div>
                  
                  {/* Timezone & Currency Section */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-white/10">
                    <div>
                      <Label htmlFor="timezone" className="flex items-center gap-2 mb-2">
                        <Clock className="w-4 h-4 text-viraly-orange" />
                        Timezone
                      </Label>
                      {profileEditMode ? (
                        <>
                          <Select value={selectedTimezone} onValueChange={setSelectedTimezone}>
                            <SelectTrigger className="input-base" data-testid="timezone-select">
                              <SelectValue placeholder="Select timezone" />
                            </SelectTrigger>
                            <SelectContent className="bg-viraly-card border-white/10 max-h-[300px]">
                              {TIMEZONE_OPTIONS.map((group) => (
                                <div key={group.group}>
                                  <div className="px-2 py-1.5 text-xs font-semibold text-viraly-orange">
                                    {group.group}
                                  </div>
                                  {group.timezones.map((tz) => (
                                    <SelectItem 
                                      key={tz.value} 
                                      value={tz.value}
                                      className="text-white hover:bg-white/10"
                                    >
                                      {tz.label}
                                    </SelectItem>
                                  ))}
                                </div>
                              ))}
                            </SelectContent>
                          </Select>
                          <p className="text-xs text-viraly-muted mt-1">Used for scheduling posts in your local time</p>
                        </>
                      ) : (
                        <p className="p-3 rounded-lg bg-white/5 text-viraly-light">
                          {TIMEZONE_OPTIONS.flatMap(g => g.timezones).find(tz => tz.value === selectedTimezone)?.label || selectedTimezone || "Not set"}
                        </p>
                      )}
                    </div>
                    
                    <div>
                      <Label htmlFor="currency" className="flex items-center gap-2 mb-2">
                        <DollarSign className="w-4 h-4 text-viraly-orange" />
                        Currency
                      </Label>
                      {profileEditMode ? (
                        <>
                          <Select value={selectedCurrency} onValueChange={setSelectedCurrency}>
                            <SelectTrigger className="input-base" data-testid="currency-select">
                              <SelectValue placeholder="Select currency" />
                            </SelectTrigger>
                            <SelectContent className="bg-viraly-card border-white/10">
                              {CURRENCY_OPTIONS.map((curr) => (
                                <SelectItem 
                                  key={curr.value} 
                                  value={curr.value}
                                  className="text-white hover:bg-white/10"
                                >
                                  {curr.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <p className="text-xs text-viraly-muted mt-1">Used for displaying prices</p>
                        </>
                      ) : (
                        <p className="p-3 rounded-lg bg-white/5 text-viraly-light">
                          {CURRENCY_OPTIONS.find(c => c.value === selectedCurrency)?.label || selectedCurrency || "Not set"}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 pt-2">
                    <Globe className="w-4 h-4 text-viraly-muted" />
                    <span className="text-sm text-viraly-muted">Country: {user?.country || "Not set"}</span>
                  </div>
                </div>
                
                {profileEditMode && (
                  <div className="flex items-center gap-3 pt-4 border-t border-white/10">
                    <Button 
                      onClick={async () => {
                        await handleSaveProfile();
                        setProfileEditMode(false);
                      }}
                      disabled={loading}
                      className="btn-primary"
                      data-testid="save-profile-btn"
                    >
                      {loading ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Save className="w-4 h-4 mr-2" />
                      )}
                      Save Changes
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => {
                        if (originalProfile) {
                          setProfile({ ...profile, name: originalProfile.name });
                          setSelectedTimezone(originalProfile.timezone);
                          setSelectedCurrency(originalProfile.currency);
                        }
                        setProfileEditMode(false);
                      }}
                      className="text-viraly-muted hover:text-white"
                    >
                      <X className="w-4 h-4 mr-2" />
                      Cancel
                    </Button>
                  </div>
                )}
              </div>
            </motion.div>

            {/* Loyalty & Rewards Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="card-base p-6 mt-6"
            >
              <h2 className="font-heading text-xl font-bold mb-6 flex items-center gap-2">
                <Gift className="w-5 h-5 text-viraly-orange" />
                Loyalty & Rewards
              </h2>
              
              <LoyaltySection user={user} />
            </motion.div>
          </TabsContent>

          <TabsContent value="brand">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="card-base p-6"
            >
              {/* No Profile Selected - Prompt user to select one */}
              {!activeProfile ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 rounded-full bg-viraly-orange/20 flex items-center justify-center mx-auto mb-4">
                    <Building2 className="w-8 h-8 text-viraly-orange" />
                  </div>
                  <h3 className="font-heading text-xl font-bold mb-2">No Profile Selected</h3>
                  <p className="text-viraly-muted mb-6 max-w-md mx-auto">
                    Select a brand profile from the header dropdown or go to Manage Profiles to create your first profile.
                  </p>
                  <Button
                    onClick={() => setActiveTab("profile")}
                    className="btn-primary"
                    data-testid="go-to-profiles-btn"
                  >
                    <Building2 className="w-4 h-4 mr-2" />
                    Manage Profiles
                  </Button>
                </div>
              ) : (
              <>
              {/* Active Profile Indicator */}
              {activeProfile && (
                <div className="mb-4 p-3 rounded-lg bg-viraly-orange/10 border border-viraly-orange/30 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {activeProfile.headshot_url ? (
                      <img
                        src={activeProfile.headshot_url}
                        alt={activeProfile.name}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-viraly-gradient flex items-center justify-center">
                        <span className="text-sm font-bold text-white">
                          {activeProfile.name?.charAt(0).toUpperCase()}
                        </span>
                      </div>
                    )}
                    <div>
                      <p className="font-medium text-sm">Editing: <span className="text-viraly-orange">{activeProfile.name}</span></p>
                      <p className="text-xs text-viraly-muted">{activeProfile.niche || 'No category set'}</p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setActiveTab("profile")}
                    className="text-viraly-muted hover:text-white text-xs"
                  >
                    Switch Profile
                  </Button>
                </div>
              )}
              
              <h2 className="font-heading text-xl font-bold mb-2">Brand Settings</h2>
              <p className="text-sm text-viraly-muted mb-6">
                Configure your brand identity to personalize all AI-generated content
              </p>
              
              {loadingProfileData ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-viraly-orange" />
                </div>
              ) : (
                <>
              {/* Auto-Learn Brand from URL */}
              <div className="mb-6 p-4 rounded-xl bg-gradient-to-r from-viraly-orange/10 to-purple-500/10 border border-viraly-orange/30">
                <h3 className="font-heading text-lg font-bold mb-2 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-viraly-orange" />
                  Auto-Learn Your Brand
                </h3>
                <p className="text-sm text-viraly-muted mb-4">
                  Enter any URL and let AI analyze your brand automatically
                </p>
                <div className="flex gap-3">
                  <Input
                    placeholder="Any URL - website, Facebook page, Instagram profile, LinkedIn..."
                    value={brandUrl}
                    onChange={(e) => setBrandUrl(e.target.value)}
                    className="input-base flex-1"
                    data-testid="brand-url-input"
                  />
                  <Button 
                    onClick={handleLearnBrand}
                    disabled={learningBrand || !brandUrl}
                    className="btn-primary"
                    data-testid="learn-brand-btn"
                  >
                    {learningBrand ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Learning...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4 mr-2" />
                        Learn Brand
                      </>
                    )}
                  </Button>
                </div>
              </div>
              
              {/* Profile Picture & Logo - PROFILE-SPECIFIC */}
              <div className="mb-8 p-4 rounded-xl bg-white/5 border border-white/10">
                <h3 className="font-heading text-lg font-bold mb-2 flex items-center gap-2">
                  <Camera className="w-5 h-5 text-viraly-orange" />
                  Profile Picture & Logo
                </h3>
                <p className="text-sm text-viraly-muted mb-4">
                  These images are specific to <span className="font-semibold text-viraly-orange">{activeProfile?.name}</span> and will appear on posts created for this profile.
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Profile Picture / Headshot Upload - Profile Level */}
                  <div 
                    className={`p-4 rounded-xl border-2 transition-all cursor-pointer ${
                      activeProfile?.brand_type !== "business" 
                        ? "bg-viraly-orange/10 border-viraly-orange" 
                        : "bg-white/5 border-white/10 hover:border-white/20"
                    }`}
                    onClick={async () => {
                      if (!activeProfile?.id || activeProfile?.brand_type !== "business") return;
                      try {
                        await apiClient.put(`/users/profiles/${activeProfile.id}`, { brand_type: "personal" });
                        await refreshProfiles();
                        toast.success("Now using: Face of Brand");
                      } catch (e) {
                        toast.error("Failed to update");
                      }
                    }}
                  >
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          activeProfile?.brand_type !== "business" ? "bg-viraly-orange/20" : "bg-blue-500/20"
                        }`}>
                          <User className={`w-5 h-5 ${activeProfile?.brand_type !== "business" ? "text-viraly-orange" : "text-blue-400"}`} />
                        </div>
                        <div>
                          <h4 className="font-medium">Face of Brand</h4>
                          <p className="text-xs text-viraly-muted">Your headshot/photo</p>
                        </div>
                      </div>
                      {activeProfile?.brand_type !== "business" && (
                        <span className="text-xs bg-viraly-orange/20 text-viraly-orange px-2 py-1 rounded-full">Active</span>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-4">
                      <div className="w-20 h-20 rounded-full bg-white/10 border-2 border-dashed border-white/20 flex items-center justify-center overflow-hidden flex-shrink-0">
                        {activeProfile?.headshot_url ? (
                          <img 
                            src={activeProfile.headshot_url.startsWith('/api') 
                              ? `${process.env.REACT_APP_BACKEND_URL}${activeProfile.headshot_url}` 
                              : activeProfile.headshot_url
                            } 
                            alt="Profile" 
                            className="w-full h-full object-cover object-center"
                          />
                        ) : (
                          <Camera className="w-8 h-8 text-viraly-muted" />
                        )}
                      </div>
                      
                      <div className="flex-1">
                        <label className="cursor-pointer" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={async (e) => {
                              const file = e.target.files?.[0];
                              if (!file || !activeProfile?.id) return;
                              if (!file.type.startsWith("image/")) {
                                toast.error("Please upload an image file");
                                return;
                              }
                              if (file.size > 5 * 1024 * 1024) {
                                toast.error("File too large. Max 5MB");
                                return;
                              }
                              try {
                                const formData = new FormData();
                                formData.append("file", file);
                                await apiClient.post(`/users/profiles/${activeProfile.id}/headshot`, formData, {
                                  headers: { "Content-Type": "multipart/form-data" }
                                });
                                // Also set brand_type to personal when uploading headshot
                                await apiClient.put(`/users/profiles/${activeProfile.id}`, { brand_type: "personal" });
                                await refreshProfiles();
                                toast.success("Headshot uploaded! Now using Face of Brand");
                              } catch (err) {
                                toast.error("Failed to upload headshot");
                              }
                            }}
                            className="hidden"
                            data-testid="profile-headshot-upload"
                          />
                          <div className="flex items-center justify-center gap-2 px-4 py-2 rounded-lg border border-white/20 hover:bg-white/10 transition-colors">
                            <Upload className="w-4 h-4" />
                            <span className="text-sm">{activeProfile?.headshot_url ? 'Change' : 'Upload'}</span>
                          </div>
                        </label>
                        <p className="text-xs text-viraly-muted mt-2">
                          JPG, PNG or WebP. Max 5MB.
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Logo Upload - Profile Level */}
                  <div 
                    className={`p-4 rounded-xl border-2 transition-all cursor-pointer ${
                      activeProfile?.brand_type === "business" 
                        ? "bg-purple-500/10 border-purple-500" 
                        : "bg-white/5 border-white/10 hover:border-white/20"
                    }`}
                    onClick={async () => {
                      if (!activeProfile?.id || activeProfile?.brand_type === "business") return;
                      try {
                        await apiClient.put(`/users/profiles/${activeProfile.id}`, { brand_type: "business" });
                        await refreshProfiles();
                        toast.success("Now using: Company Logo");
                      } catch (e) {
                        toast.error("Failed to update");
                      }
                    }}
                  >
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          activeProfile?.brand_type === "business" ? "bg-purple-500/20" : "bg-purple-500/10"
                        }`}>
                          <Building2 className={`w-5 h-5 ${activeProfile?.brand_type === "business" ? "text-purple-400" : "text-purple-300"}`} />
                        </div>
                        <div>
                          <h4 className="font-medium">Company Logo</h4>
                          <p className="text-xs text-viraly-muted">Your business logo</p>
                        </div>
                      </div>
                      {activeProfile?.brand_type === "business" && (
                        <span className="text-xs bg-purple-500/20 text-purple-400 px-2 py-1 rounded-full">Active</span>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-4">
                      <div className="w-20 h-20 rounded-lg bg-white/10 border-2 border-dashed border-white/20 flex items-center justify-center overflow-hidden">
                        {activeProfile?.logo_url ? (
                          <img 
                            src={activeProfile.logo_url.startsWith('/api') 
                              ? `${process.env.REACT_APP_BACKEND_URL}${activeProfile.logo_url}` 
                              : activeProfile.logo_url
                            } 
                            alt="Logo" 
                            className="w-full h-full object-contain p-1"
                          />
                        ) : (
                          <Building2 className="w-8 h-8 text-viraly-muted" />
                        )}
                      </div>
                      
                      <div className="flex-1">
                        <label className="cursor-pointer" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={async (e) => {
                              const file = e.target.files?.[0];
                              if (!file || !activeProfile?.id) return;
                              if (!file.type.startsWith("image/")) {
                                toast.error("Please upload an image file");
                                return;
                              }
                              if (file.size > 5 * 1024 * 1024) {
                                toast.error("File too large. Max 5MB");
                                return;
                              }
                              try {
                                const formData = new FormData();
                                formData.append("file", file);
                                await apiClient.post(`/users/profiles/${activeProfile.id}/logo`, formData, {
                                  headers: { "Content-Type": "multipart/form-data" }
                                });
                                // Also set brand_type to business when uploading logo
                                await apiClient.put(`/users/profiles/${activeProfile.id}`, { brand_type: "business" });
                                await refreshProfiles();
                                toast.success("Logo uploaded! Now using Company Logo");
                              } catch (err) {
                                toast.error("Failed to upload logo");
                              }
                            }}
                            className="hidden"
                            data-testid="profile-logo-upload"
                          />
                          <div className="flex items-center justify-center gap-2 px-4 py-2 rounded-lg border border-white/20 hover:bg-white/10 transition-colors">
                            <Upload className="w-4 h-4" />
                            <span className="text-sm">{activeProfile?.logo_url ? 'Change' : 'Upload'}</span>
                          </div>
                        </label>
                        <p className="text-xs text-viraly-muted mt-2">
                          JPG, PNG, WebP or SVG. Max 5MB.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                
                {(activeProfile?.headshot_url || activeProfile?.logo_url) && (
                  <p className="mt-4 text-sm text-viraly-success flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    Your images will appear on posts when you select "My Brand" style
                  </p>
                )}
              </div>
              
              {/* Brand Voice & Other Settings */}
              <div className="space-y-6">
                <div>
                  <Label htmlFor="voice">Brand Voice & Tone</Label>
                  <p className="text-sm text-viraly-muted mb-2">
                    How does your brand communicate? (e.g., professional, casual, witty, inspirational)
                  </p>
                  <Textarea
                    id="voice"
                    value={profileBrandData.brand_voice}
                    onChange={(e) => setProfileBrandData({ ...profileBrandData, brand_voice: e.target.value })}
                    className="input-base"
                    placeholder="Example: Professional yet approachable, uses occasional humor, empowers customers with knowledge..."
                    rows={3}
                    data-testid="brand-voice-input"
                  />
                </div>
                
                <div>
                  <Label htmlFor="audience">Target Audience</Label>
                  <p className="text-sm text-viraly-muted mb-2">
                    Who are you creating content for? Describe their demographics, interests, and pain points
                  </p>
                  <Textarea
                    id="audience"
                    value={profileBrandData.target_audience}
                    onChange={(e) => setProfileBrandData({ ...profileBrandData, target_audience: e.target.value })}
                    className="input-base"
                    placeholder="Example: Small business owners aged 25-45 in North America, interested in marketing automation and growth hacks, struggling with limited time and resources..."
                    rows={3}
                    data-testid="target-audience-input"
                  />
                </div>
                
                <div>
                  <Label htmlFor="keywords">Brand Keywords & Phrases</Label>
                  <p className="text-sm text-viraly-muted mb-2">
                    Key terms, taglines, or phrases unique to your brand that should appear in content
                  </p>
                  <Textarea
                    id="keywords"
                    value={profileBrandData.brand_keywords || ""}
                    onChange={(e) => setProfileBrandData({ ...profileBrandData, brand_keywords: e.target.value })}
                    className="input-base"
                    placeholder="Example: 'Level up your game', 'Work smarter not harder', AI-powered, effortless, authentic..."
                    rows={2}
                    data-testid="brand-keywords-input"
                  />
                </div>
                
                <div>
                  <Label htmlFor="avoid">Content to Avoid</Label>
                  <p className="text-sm text-viraly-muted mb-2">
                    Topics, phrases, or styles that don't fit your brand (competitors, certain language, etc.)
                  </p>
                  <Textarea
                    id="avoid"
                    value={profileBrandData.content_avoid || ""}
                    onChange={(e) => setProfileBrandData({ ...profileBrandData, content_avoid: e.target.value })}
                    className="input-base"
                    placeholder="Example: Avoid mentioning competitors by name, no aggressive sales language, don't use slang or emojis excessively..."
                    rows={2}
                    data-testid="content-avoid-input"
                  />
                </div>
                
                {/* Email for this Profile */}
                <div className="p-4 rounded-xl bg-blue-500/10 border border-blue-500/20">
                  <div className="flex items-center gap-2 mb-2">
                    <Mail className="w-5 h-5 text-blue-400" />
                    <Label htmlFor="sender_email" className="text-blue-400 font-medium">Sender Email for this Profile</Label>
                  </div>
                  <p className="text-sm text-viraly-muted mb-3">
                    When sharing content via email from this profile, which email address should it be sent from?
                  </p>
                  <Input 
                    id="sender_email"
                    type="email"
                    value={profileBrandData.sender_email || ""}
                    onChange={(e) => setProfileBrandData({ ...profileBrandData, sender_email: e.target.value })}
                    className="input-base"
                    placeholder="e.g., marketing@mybusiness.com"
                    data-testid="sender-email-input"
                  />
                  <p className="text-xs text-viraly-muted mt-2">
                    💡 You can connect different Gmail accounts for different profiles to send emails with the right sender identity.
                  </p>
                </div>
                
                <div className="p-4 rounded-xl bg-viraly-orange/10 border border-viraly-orange/20">
                  <p className="text-sm text-viraly-orange font-medium flex items-center gap-2">
                    <Sparkles className="w-4 h-4" />
                    Pro Tip: The more detail you provide, the better AI will match your brand!
                  </p>
                </div>
                
                <Button 
                  onClick={handleSaveProfileBrand} 
                  disabled={loading || !activeProfile?.id}
                  className="btn-primary"
                  data-testid="save-brand-btn"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Save Brand Settings for {activeProfile?.name || 'Profile'}
                </Button>
              </div>
              </>
              )}
              </>
              )}
            </motion.div>
          </TabsContent>

          <TabsContent value="billing">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              {/* Credit Summary Card */}
              <div className="card-base p-6">
                <h2 className="font-heading text-xl font-bold mb-6">Credit Summary</h2>
                
                {loadingPlans ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-viraly-orange" />
                  </div>
                ) : creditSummary ? (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="p-4 rounded-xl bg-white/5">
                      <p className="text-viraly-muted text-sm">Total Posts</p>
                      <p className="text-2xl font-heading font-bold gradient-text">
                        {creditSummary.total_posts || 0}
                      </p>
                      {creditSummary.payg_posts > 0 && (
                        <p className="text-xs text-viraly-muted mt-1">
                          ({creditSummary.plan_posts} plan + {creditSummary.payg_posts} PAYG)
                        </p>
                      )}
                    </div>
                    <div className="p-4 rounded-xl bg-white/5">
                      <p className="text-viraly-muted text-sm">Video Credits</p>
                      <p className="text-2xl font-heading font-bold text-viraly-success">
                        {creditSummary.total_video_credits || 0}
                      </p>
                      <p className="text-xs text-viraly-muted mt-1">
                        {creditSummary.has_1080p_access ? "1080p HD" : creditSummary.has_720p_access ? "720p" : "No video"}
                      </p>
                    </div>
                    <div className="p-4 rounded-xl bg-white/5">
                      <p className="text-viraly-muted text-sm">Regen Credits</p>
                      <p className="text-2xl font-heading font-bold text-blue-400">
                        {creditSummary.total_regen_credits || 0}
                      </p>
                    </div>
                    <div className="p-4 rounded-xl bg-white/5">
                      <p className="text-viraly-muted text-sm">Active Plans</p>
                      <p className="text-2xl font-heading font-bold text-viraly-orange">
                        {creditSummary.active_plans_count || 0}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="p-4 rounded-xl bg-white/5">
                      <p className="text-viraly-muted text-sm">Posts Remaining</p>
                      <p className="text-2xl font-heading font-bold gradient-text">
                        {user?.posts_remaining || 0}
                      </p>
                    </div>
                    <div className="p-4 rounded-xl bg-white/5">
                      <p className="text-viraly-muted text-sm">Videos Remaining</p>
                      <p className="text-2xl font-heading font-bold text-viraly-success">
                        {user?.video_credits_remaining || 0}
                      </p>
                    </div>
                    <div className="p-4 rounded-xl bg-white/5">
                      <p className="text-viraly-muted text-sm">Regen Credits</p>
                      <p className="text-2xl font-heading font-bold text-blue-400">
                        {user?.regen_credits || 0}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Active Plans with Expiry Dates */}
              <div className="card-base p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="font-heading text-xl font-bold">Active Plans</h2>
                  <Button 
                    onClick={() => window.location.href = "/pricing"}
                    className="btn-primary"
                    data-testid="upgrade-plan-btn"
                  >
                    Add Plan
                  </Button>
                </div>
                
                {loadingPlans ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-viraly-orange" />
                  </div>
                ) : activePlans.length > 0 ? (
                  <div className="space-y-4">
                    {activePlans.map((plan) => (
                      <div 
                        key={plan.id} 
                        className={`p-4 rounded-xl border ${
                          plan.days_remaining <= 7 
                            ? 'bg-red-500/10 border-red-500/30' 
                            : plan.days_remaining <= 14 
                              ? 'bg-yellow-500/10 border-yellow-500/30'
                              : 'bg-white/5 border-white/10'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-bold text-lg">{plan.plan_name}</p>
                              {plan.video_quality && (
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                  plan.video_quality === "1080p" 
                                    ? "bg-viraly-orange/20 text-viraly-orange" 
                                    : "bg-blue-500/20 text-blue-400"
                                }`}>
                                  {plan.video_quality}
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-viraly-muted mt-1">
                              Purchased: {new Date(plan.purchased_at).toLocaleDateString()}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className={`font-bold ${
                              plan.days_remaining <= 7 
                                ? 'text-red-400' 
                                : plan.days_remaining <= 14 
                                  ? 'text-yellow-400'
                                  : 'text-viraly-success'
                            }`}>
                              {plan.days_remaining} days left
                            </p>
                            <p className="text-xs text-viraly-muted">
                              Expires: {new Date(plan.expires_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        
                        {/* Credits breakdown */}
                        <div className="grid grid-cols-3 gap-3 mt-4 pt-4 border-t border-white/10">
                          <div>
                            <p className="text-xs text-viraly-muted">Posts</p>
                            <p className="font-bold">{plan.posts_remaining}</p>
                          </div>
                          <div>
                            <p className="text-xs text-viraly-muted">Videos</p>
                            <p className="font-bold">{plan.video_credits_remaining}</p>
                          </div>
                          <div>
                            <p className="text-xs text-viraly-muted">Regen</p>
                            <p className="font-bold">{plan.regen_credits}</p>
                          </div>
                        </div>
                        
                        {/* Expiry warning */}
                        {plan.days_remaining <= 7 && (
                          <div className="mt-3 p-2 rounded-lg bg-red-500/20 text-red-400 text-sm flex items-center gap-2">
                            <Clock className="w-4 h-4" />
                            <span>Expiring soon! Use your credits or renew.</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-viraly-muted mb-4">No active plans</p>
                    <Button 
                      onClick={() => window.location.href = "/pricing"}
                      className="btn-primary"
                    >
                      View Plans
                    </Button>
                  </div>
                )}
                
                {/* Note about concurrent plans */}
                {activePlans.length > 0 && (
                  <div className="mt-4 p-3 rounded-lg bg-viraly-orange/10 border border-viraly-orange/20">
                    <p className="text-sm text-viraly-muted">
                      <strong className="text-viraly-orange">Pro tip:</strong> All your plans run concurrently. Credits are consumed from soonest-expiring plans first to maximize usage.
                    </p>
                  </div>
                )}
              </div>
            </motion.div>
          </TabsContent>

          <TabsContent value="notifications">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              {/* All Notification Types - Individual Toggles */}
              <div className="card-base p-6">
                <h2 className="font-heading text-xl font-bold mb-2">Notification Preferences</h2>
                <p className="text-viraly-muted mb-6">
                  All notifications are enabled by default. Disabling any notification may affect your experience.
                </p>
                
                <div className="space-y-4">
                  {/* Push Notification Types */}
                  <div className="pb-4 border-b border-white/10">
                    <p className="text-sm font-medium text-viraly-orange mb-3 flex items-center gap-2">
                      <BellRing className="w-4 h-4" />
                      Push Notifications
                    </p>
                    {[
                      { id: "push_content_ready", label: "Content Ready", desc: "When AI-generated content is ready for review", warning: "You won't know when your content is ready" },
                      { id: "push_scheduled_posts", label: "Post Published", desc: "When scheduled posts are published", warning: "You won't be notified when posts go live" },
                      { id: "push_credit_low", label: "Low Credits Alert", desc: "When your credits are running low", warning: "You may run out of credits unexpectedly" },
                      { id: "push_engagement_alerts", label: "Engagement Alerts", desc: "High engagement on your posts", warning: "You'll miss viral moment notifications" }
                    ].map((item) => (
                      <NotificationToggleWithWarning key={item.id} item={item} />
                    ))}
                  </div>

                  {/* Email Notification Types */}
                  <div className="pt-2">
                    <p className="text-sm font-medium text-viraly-orange mb-3 flex items-center gap-2">
                      <Mail className="w-4 h-4" />
                      Email Notifications
                    </p>
                    {[
                      { id: "email_weekly_digest", label: "Weekly Snapshot", desc: "Weekly summary of posts created & published", warning: "You'll miss your weekly performance report" },
                      { id: "email_product_updates", label: "Tips & Updates", desc: "Product updates and social media tips", warning: "You'll miss new features and tips" },
                      { id: "email_marketing", label: "Promotional Offers", desc: "Special deals and promotional offers", warning: "You may miss exclusive discounts" }
                    ].map((item) => (
                      <NotificationToggleWithWarning key={item.id} item={item} />
                    ))}
                  </div>
                </div>
              </div>
              
              {/* Auto-Posting Permissions - For Auto-Pilot & Bulk */}
              <div className="card-base p-6">
                <h2 className="font-heading text-xl font-bold mb-2 flex items-center gap-2">
                  <Shield className="w-5 h-5 text-viraly-orange" />
                  Auto-Posting Permissions
                </h2>
                <p className="text-viraly-muted mb-6">
                  Configure default settings for Auto-Pilot and Bulk posting. When enabled, posts will use these settings automatically.
                </p>
                
                {loadingAutoSettings ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-viraly-orange" />
                    <span className="ml-2 text-viraly-muted">Loading settings...</span>
                  </div>
                ) : (
                <div className="space-y-6">
                  {/* TikTok Auto-Posting Settings */}
                  <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-pink-500 to-blue-500 flex items-center justify-center">
                          <span className="text-white font-bold text-sm">TT</span>
                        </div>
                        <div>
                          <p className="font-medium">TikTok Auto-Posting</p>
                          <p className="text-xs text-viraly-muted">Default settings for automated TikTok posts</p>
                        </div>
                      </div>
                      <Button 
                        size="sm" 
                        onClick={saveTiktokAutoSettings}
                        disabled={savingAutoSettings}
                        className="btn-primary"
                      >
                        {savingAutoSettings ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
                      </Button>
                    </div>
                    
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm">Default Privacy</span>
                        <select 
                          className="input-base w-40 text-sm"
                          value={tiktokAutoSettings.default_privacy_level}
                          onChange={(e) => setTiktokAutoSettings({...tiktokAutoSettings, default_privacy_level: e.target.value})}
                          data-testid="tiktok-default-privacy"
                        >
                          <option value="">Select...</option>
                          <option value="PUBLIC_TO_EVERYONE">Everyone</option>
                          <option value="MUTUAL_FOLLOW_FRIENDS">Friends</option>
                          <option value="FOLLOWER_OF_CREATOR">Followers</option>
                          <option value="SELF_ONLY">Only Me</option>
                        </select>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm">Allow Comments</span>
                        <input 
                          type="checkbox" 
                          className="w-4 h-4 rounded"
                          checked={tiktokAutoSettings.allow_comment}
                          onChange={(e) => setTiktokAutoSettings({...tiktokAutoSettings, allow_comment: e.target.checked})}
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm">Allow Duet</span>
                        <input 
                          type="checkbox" 
                          className="w-4 h-4 rounded"
                          checked={tiktokAutoSettings.allow_duet}
                          onChange={(e) => setTiktokAutoSettings({...tiktokAutoSettings, allow_duet: e.target.checked})}
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm">Allow Stitch</span>
                        <input 
                          type="checkbox" 
                          className="w-4 h-4 rounded"
                          checked={tiktokAutoSettings.allow_stitch}
                          onChange={(e) => setTiktokAutoSettings({...tiktokAutoSettings, allow_stitch: e.target.checked})}
                        />
                      </div>
                      <div className="pt-3 border-t border-white/10">
                        <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                          <div className="flex items-start gap-2">
                            <Bell className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                            <div>
                              <p className="text-sm text-amber-400 font-medium">Per-Post Approval Required</p>
                              <p className="text-xs text-amber-400/80 mt-1">
                                Per TikTok's API guidelines, you must approve each post before it's published to TikTok. 
                                For Auto-Pilot posts, you'll receive a push notification to approve the privacy settings. 
                                The settings above will be used as defaults to pre-fill each approval.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Pinterest Auto-Posting Settings */}
                  <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center">
                          <span className="text-white font-bold text-sm">P</span>
                        </div>
                        <div>
                          <p className="font-medium">Pinterest Auto-Posting</p>
                          <p className="text-xs text-viraly-muted">Default board for automated Pinterest pins</p>
                        </div>
                      </div>
                      <Button 
                        size="sm" 
                        onClick={savePinterestAutoSettings}
                        disabled={savingAutoSettings}
                        className="btn-primary"
                      >
                        {savingAutoSettings ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
                      </Button>
                    </div>
                    
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm">Default Board</span>
                        <select 
                          className="input-base w-40 text-sm"
                          value={pinterestAutoSettings.default_board_id}
                          onChange={(e) => {
                            const board = pinterestBoards.find(b => b.id === e.target.value);
                            setPinterestAutoSettings({
                              ...pinterestAutoSettings, 
                              default_board_id: e.target.value,
                              default_board_name: board?.name || ""
                            });
                          }}
                          data-testid="pinterest-default-board"
                        >
                          <option value="">Select board...</option>
                          {pinterestBoards.map(board => (
                            <option key={board.id} value={board.id}>{board.name}</option>
                          ))}
                        </select>
                      </div>
                      <div className="pt-3 border-t border-white/10">
                        <label className="flex items-start gap-3 cursor-pointer">
                          <input 
                            type="checkbox" 
                            className="w-4 h-4 rounded mt-1"
                            checked={pinterestAutoSettings.auto_posting_consent}
                            onChange={(e) => setPinterestAutoSettings({...pinterestAutoSettings, auto_posting_consent: e.target.checked})}
                            data-testid="pinterest-auto-consent"
                          />
                          <span className="text-sm text-viraly-muted">
                            I authorize Viraly to automatically post to Pinterest using these default settings for Auto-Pilot and Bulk posts.
                          </span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
                )}
              </div>
            </motion.div>
          </TabsContent>

          {/* Social Accounts Tab */}
          <TabsContent value="social">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="card-base p-6"
            >
              {/* Active Profile Indicator for Social Tab */}
              {activeProfile && (
                <div className="mb-4 p-3 rounded-lg bg-blue-500/10 border border-blue-500/30 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {activeProfile.headshot_url ? (
                      <img
                        src={activeProfile.headshot_url}
                        alt={activeProfile.name}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                        <span className="text-sm font-bold text-blue-400">
                          {activeProfile.name?.charAt(0).toUpperCase()}
                        </span>
                      </div>
                    )}
                    <div>
                      <p className="font-medium text-sm">Connecting accounts for: <span className="text-blue-400">{activeProfile.name}</span></p>
                      <p className="text-xs text-viraly-muted">Social accounts are profile-specific</p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setActiveTab("profile")}
                    className="text-viraly-muted hover:text-white text-xs"
                  >
                    Switch Profile
                  </Button>
                </div>
              )}
              
              <h2 className="font-heading text-xl font-bold mb-2">Social Media Accounts</h2>
              <p className="text-viraly-muted mb-6">
                Connect your social accounts to publish content directly from Viraly
              </p>
              
              <div className="space-y-4">
                {/* Facebook */}
                <div className="flex items-center justify-between p-4 rounded-xl bg-white/5 border border-white/10">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-blue-600 flex items-center justify-center">
                      <Facebook className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="font-medium">Facebook</p>
                      <p className="text-sm text-viraly-muted">
                        {(profileSocialStatus || socialStatus)?.facebook?.connected 
                          ? (
                            <>
                              {(profileSocialStatus || socialStatus).facebook.accounts?.[0]?.handle && (
                                <span className="text-blue-400 font-medium">
                                  @{(profileSocialStatus || socialStatus).facebook.accounts[0].handle}
                                </span>
                              )}
                              {(profileSocialStatus || socialStatus).facebook.accounts?.[0]?.page_name && !((profileSocialStatus || socialStatus).facebook.accounts?.[0]?.handle) && (
                                <span className="text-blue-400 font-medium">
                                  {(profileSocialStatus || socialStatus).facebook.accounts[0].page_name}
                                </span>
                              )}
                              {(profileSocialStatus || socialStatus).facebook.accounts?.[0]?.pages?.length > 0 && (
                                <span className="ml-1">
                                  • {(profileSocialStatus || socialStatus).facebook.accounts[0].pages.length} page(s)
                                </span>
                              )}
                              {!((profileSocialStatus || socialStatus).facebook.accounts?.[0]?.handle || (profileSocialStatus || socialStatus).facebook.accounts?.[0]?.page_name) && (
                                <span>Account connected</span>
                              )}
                            </>
                          )
                          : "Connect to post to Pages"}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {(profileSocialStatus || socialStatus)?.facebook?.connected ? (
                      <>
                        <CheckCircle className="w-5 h-5 text-viraly-success" />
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDisconnect("Facebook", (profileSocialStatus || socialStatus).facebook.accounts[0]?.id)}
                          className="text-viraly-muted hover:text-viraly-error"
                        >
                          Disconnect
                        </Button>
                      </>
                    ) : (
                      <Button
                        onClick={() => handleConnectPlatform("facebook")}
                        disabled={connectingPlatform === "facebook"}
                        className="btn-secondary"
                      >
                        {connectingPlatform === "facebook" ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        ) : null}
                        Connect
                      </Button>
                    )}
                  </div>
                </div>

                {/* Instagram */}
                <div className="flex items-center justify-between p-4 rounded-xl bg-white/5 border border-white/10">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400 flex items-center justify-center">
                      <Instagram className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="font-medium">Instagram</p>
                      <p className="text-sm text-viraly-muted">
                        {(profileSocialStatus || socialStatus)?.instagram?.connected 
                          ? (
                            <>
                              {(profileSocialStatus || socialStatus).instagram.accounts?.[0]?.handle && (
                                <span className="text-pink-400 font-medium">
                                  @{(profileSocialStatus || socialStatus).instagram.accounts[0].handle}
                                </span>
                              )}
                              {(profileSocialStatus || socialStatus).instagram.accounts?.[0]?.username && !((profileSocialStatus || socialStatus).instagram.accounts?.[0]?.handle) && (
                                <span className="text-pink-400 font-medium">
                                  @{(profileSocialStatus || socialStatus).instagram.accounts[0].username}
                                </span>
                              )}
                              {!((profileSocialStatus || socialStatus).instagram.accounts?.[0]?.handle || (profileSocialStatus || socialStatus).instagram.accounts?.[0]?.username) && (
                                <span>Business account connected</span>
                              )}
                            </>
                          )
                          : "Connect your Business account"}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {(profileSocialStatus || socialStatus)?.instagram?.connected ? (
                      <>
                        <CheckCircle className="w-5 h-5 text-viraly-success" />
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDisconnect("Instagram", (profileSocialStatus || socialStatus).instagram.accounts[0]?.id)}
                          className="text-viraly-muted hover:text-viraly-error"
                        >
                          Disconnect
                        </Button>
                      </>
                    ) : (
                      <Button
                        onClick={() => handleConnectPlatform("instagram")}
                        disabled={connectingPlatform === "instagram"}
                        className="btn-secondary"
                      >
                        {connectingPlatform === "instagram" ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        ) : null}
                        Connect
                      </Button>
                    )}
                  </div>
                </div>

                {/* TikTok */}
                <div className="flex items-center justify-between p-4 rounded-xl bg-white/5 border border-white/10">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-black flex items-center justify-center border border-white/20">
                      <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
                      </svg>
                    </div>
                    <div>
                      <p className="font-medium">TikTok</p>
                      <p className="text-sm text-viraly-muted">
                        {(profileSocialStatus || socialStatus)?.tiktok?.connected 
                          ? (
                            <>
                              {(profileSocialStatus || socialStatus).tiktok.accounts?.[0]?.handle && (
                                <span className="text-cyan-400 font-medium">
                                  @{(profileSocialStatus || socialStatus).tiktok.accounts[0].handle}
                                </span>
                              )}
                              {!((profileSocialStatus || socialStatus).tiktok.accounts?.[0]?.handle) && (
                                <span>Account connected</span>
                              )}
                            </>
                          )
                          : "Connect your TikTok account"}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {(profileSocialStatus || socialStatus)?.tiktok?.connected ? (
                      <>
                        <CheckCircle className="w-5 h-5 text-viraly-success" />
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDisconnect("TikTok", (profileSocialStatus || socialStatus).tiktok.accounts[0]?.id)}
                          className="text-viraly-muted hover:text-viraly-error"
                        >
                          Disconnect
                        </Button>
                      </>
                    ) : (
                      <Button
                        onClick={() => handleConnectPlatform("tiktok")}
                        disabled={connectingPlatform === "tiktok"}
                        className="btn-secondary"
                      >
                        {connectingPlatform === "tiktok" ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        ) : null}
                        Connect
                      </Button>
                    )}
                  </div>
                </div>
                
                {/* Pinterest */}
                <div className="flex items-center justify-between p-4 rounded-xl bg-white/5 border border-white/10">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-xl bg-[#BD081C] flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 0C5.373 0 0 5.373 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738.098.119.112.224.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.632-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z"/>
                      </svg>
                    </div>
                    <div>
                      <p className="font-medium">Pinterest</p>
                      <p className="text-sm text-viraly-muted">
                        {(profileSocialStatus || socialStatus)?.pinterest?.connected 
                          ? (
                            <>
                              {(profileSocialStatus || socialStatus).pinterest.accounts?.[0]?.handle && (
                                <span className="text-red-400 font-medium">
                                  @{(profileSocialStatus || socialStatus).pinterest.accounts[0].handle}
                                </span>
                              )}
                              {!((profileSocialStatus || socialStatus).pinterest.accounts?.[0]?.handle) && (
                                <span>Account connected</span>
                              )}
                            </>
                          )
                          : "Connect your Pinterest account"}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {(profileSocialStatus || socialStatus)?.pinterest?.connected ? (
                      <>
                        <CheckCircle className="w-5 h-5 text-viraly-success" />
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDisconnect("Pinterest", (profileSocialStatus || socialStatus).pinterest.accounts[0]?.id)}
                          className="text-viraly-muted hover:text-viraly-error"
                        >
                          Disconnect
                        </Button>
                      </>
                    ) : (
                      <Button
                        onClick={() => handleConnectPlatform("pinterest")}
                        disabled={connectingPlatform === "pinterest"}
                        className="btn-secondary"
                      >
                        {connectingPlatform === "pinterest" ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        ) : null}
                        Connect
                      </Button>
                    )}
                  </div>
                </div>

                {/* LinkedIn */}
                <div className="flex items-center justify-between p-4 rounded-xl bg-white/5 border border-white/10">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-[#0A66C2] flex items-center justify-center">
                      <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                      </svg>
                    </div>
                    <div>
                      <p className="font-medium">LinkedIn</p>
                      <p className="text-sm text-viraly-muted">
                        {(profileSocialStatus || socialStatus)?.linkedin?.connected 
                          ? (
                            <>
                              {(profileSocialStatus || socialStatus).linkedin.accounts?.[0]?.handle && (
                                <span className="text-[#0A66C2] font-medium">
                                  @{(profileSocialStatus || socialStatus).linkedin.accounts[0].handle}
                                </span>
                              )}
                              {!((profileSocialStatus || socialStatus).linkedin.accounts?.[0]?.handle) && (
                                <span>Account connected</span>
                              )}
                            </>
                          )
                          : "Connect your LinkedIn profile or page"}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {(profileSocialStatus || socialStatus)?.linkedin?.connected ? (
                      <>
                        <CheckCircle className="w-5 h-5 text-viraly-success" />
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDisconnect("LinkedIn", (profileSocialStatus || socialStatus).linkedin.accounts[0]?.id)}
                          className="text-viraly-muted hover:text-viraly-error"
                        >
                          Disconnect
                        </Button>
                      </>
                    ) : (
                      <Button
                        onClick={() => handleConnectPlatform("linkedin")}
                        disabled={connectingPlatform === "linkedin"}
                        className="btn-secondary"
                      >
                        {connectingPlatform === "linkedin" ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        ) : null}
                        Connect
                      </Button>
                    )}
                  </div>
                </div>

                {/* YouTube */}
                <div className="flex items-center justify-between p-4 rounded-xl bg-white/5 border border-white/10">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-[#FF0000] flex items-center justify-center">
                      <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                      </svg>
                    </div>
                    <div>
                      <p className="font-medium">YouTube</p>
                      <p className="text-sm text-viraly-muted">
                        {(profileSocialStatus || socialStatus)?.youtube?.connected 
                          ? (
                            <>
                              {(profileSocialStatus || socialStatus).youtube.accounts?.[0]?.handle && (
                                <span className="text-[#FF0000] font-medium">
                                  @{(profileSocialStatus || socialStatus).youtube.accounts[0].handle}
                                </span>
                              )}
                              {!((profileSocialStatus || socialStatus).youtube.accounts?.[0]?.handle) && (
                                <span>Channel connected</span>
                              )}
                            </>
                          )
                          : "Connect your YouTube channel"}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {(profileSocialStatus || socialStatus)?.youtube?.connected ? (
                      <>
                        <CheckCircle className="w-5 h-5 text-viraly-success" />
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDisconnect("YouTube", (profileSocialStatus || socialStatus).youtube.accounts[0]?.id)}
                          className="text-viraly-muted hover:text-viraly-error"
                        >
                          Disconnect
                        </Button>
                      </>
                    ) : (
                      <Button
                        onClick={() => handleConnectPlatform("youtube")}
                        disabled={connectingPlatform === "youtube"}
                        className="btn-secondary"
                      >
                        {connectingPlatform === "youtube" ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        ) : null}
                        Connect
                      </Button>
                    )}
                  </div>
                </div>

                {/* X (Twitter) */}
                <div className="flex items-center justify-between p-4 rounded-xl bg-white/5 border border-white/10">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-black flex items-center justify-center border border-white/20">
                      <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                      </svg>
                    </div>
                    <div>
                      <p className="font-medium">X (Twitter)</p>
                      <p className="text-sm text-viraly-muted">
                        {(profileSocialStatus || socialStatus)?.twitter?.connected 
                          ? (
                            <>
                              {(profileSocialStatus || socialStatus).twitter.accounts?.[0]?.handle && (
                                <span className="text-white font-medium">
                                  @{(profileSocialStatus || socialStatus).twitter.accounts[0].handle}
                                </span>
                              )}
                              {!((profileSocialStatus || socialStatus).twitter.accounts?.[0]?.handle) && (
                                <span>Account connected</span>
                              )}
                            </>
                          )
                          : "Connect your X account"}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {(profileSocialStatus || socialStatus)?.twitter?.connected ? (
                      <>
                        <CheckCircle className="w-5 h-5 text-viraly-success" />
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDisconnect("Twitter", (profileSocialStatus || socialStatus).twitter.accounts[0]?.id)}
                          className="text-viraly-muted hover:text-viraly-error"
                        >
                          Disconnect
                        </Button>
                      </>
                    ) : (
                      <Button
                        onClick={() => handleConnectPlatform("twitter")}
                        disabled={connectingPlatform === "twitter"}
                        className="btn-secondary"
                      >
                        {connectingPlatform === "twitter" ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        ) : null}
                        Connect
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              <div className="mt-6 p-4 rounded-xl bg-viraly-success/10 border border-viraly-success/20">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-viraly-success flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-viraly-success">7 Platforms Supported</p>
                    <p className="text-xs text-viraly-muted mt-1">
                      Connect all your social accounts once and post everywhere. Viraly handles posting to Facebook, Instagram, TikTok, Pinterest, LinkedIn, YouTube, and X automatically.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </TabsContent>
        </Tabs>
      </div>
      
      {/* Create Profile Multi-Step Wizard Modal */}
      <AnimatePresence>
        {createProfileModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => {
              setCreateProfileModal(false);
              resetWizardState();
            }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-viraly-card rounded-2xl w-full max-w-lg overflow-hidden max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Wizard Header with Step Indicator */}
              <div className="p-6 border-b border-white/10">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-heading text-xl font-bold">
                    {createProfileStep === 1 && "Create New Profile"}
                    {createProfileStep === 2 && "Brand Learning"}
                    {createProfileStep === 3 && "Upload Images"}
                    {createProfileStep === 4 && "Connect Social Accounts"}
                  </h2>
                  <button 
                    onClick={() => {
                      setCreateProfileModal(false);
                      resetWizardState();
                    }} 
                    className="p-2 hover:bg-white/10 rounded-lg"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                
                {/* Step Progress Indicator */}
                <div className="flex items-center gap-2">
                  {[1, 2, 3, 4].map((step) => (
                    <div key={step} className="flex-1 flex items-center">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all ${
                        createProfileStep >= step 
                          ? 'bg-viraly-orange text-white' 
                          : 'bg-white/10 text-viraly-muted'
                      }`}>
                        {createProfileStep > step ? <Check className="w-4 h-4" /> : step}
                      </div>
                      {step < 4 && (
                        <div className={`flex-1 h-1 mx-2 rounded ${
                          createProfileStep > step ? 'bg-viraly-orange' : 'bg-white/10'
                        }`} />
                      )}
                    </div>
                  ))}
                </div>
                <div className="flex justify-between mt-2 text-[10px] text-viraly-muted">
                  <span>Basic Info</span>
                  <span>Brand</span>
                  <span>Images</span>
                  <span>Social</span>
                </div>
              </div>
              
              <div className="p-6">
                {/* STEP 1: Basic Profile Info */}
                {createProfileStep === 1 && (
                  <div className="space-y-4">
                    {/* Mandatory fields notice */}
                    <div className="p-3 rounded-lg bg-viraly-orange/10 border border-viraly-orange/20">
                      <p className="text-xs text-viraly-orange flex items-center gap-2">
                        <AlertCircle className="w-4 h-4 flex-shrink-0" />
                        <span>All fields are required to create high-quality, personalized content for your brand.</span>
                      </p>
                    </div>
                    
                    <div>
                      <label className="block text-sm text-viraly-muted mb-2">
                        Profile Name <span className="text-red-400">*</span>
                      </label>
                      <Input 
                        value={newProfileData.name}
                        onChange={(e) => setNewProfileData({ ...newProfileData, name: e.target.value })}
                        className={`input-base ${!newProfileData.name && 'border-red-500/30'}`}
                        placeholder="e.g., My Restaurant, Fitness Studio, Real Estate"
                        data-testid="profile-name-wizard"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm text-viraly-muted mb-2">
                        Industry/Niche <span className="text-red-400">*</span>
                      </label>
                      <select 
                        value={newProfileData.niche}
                        onChange={(e) => setNewProfileData({ ...newProfileData, niche: e.target.value })}
                        className={`input-base w-full px-3 py-2 rounded-lg ${!newProfileData.niche && 'border-red-500/30'}`}
                        data-testid="profile-niche-wizard"
                      >
                        <option value="">Select an industry</option>
                        <option value="Real Estate">Real Estate</option>
                        <option value="Restaurant / Food">Restaurant / Food</option>
                        <option value="Retail / E-commerce">Retail / E-commerce</option>
                        <option value="Fitness / Wellness">Fitness / Wellness</option>
                        <option value="Finance / Mortgage">Finance / Mortgage</option>
                        <option value="Professional Services">Professional Services</option>
                        <option value="Healthcare">Healthcare</option>
                        <option value="Education">Education</option>
                        <option value="Technology">Technology</option>
                        <option value="Travel / Hospitality">Travel / Hospitality</option>
                        <option value="Automotive">Automotive</option>
                        <option value="Beauty / Salon">Beauty / Salon</option>
                        <option value="Home Services">Home Services</option>
                        <option value="Personal Branding">Personal Branding</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm text-viraly-muted mb-2">
                        Website or Social URL <span className="text-red-400">*</span>
                      </label>
                      <Input 
                        value={newProfileData.url}
                        onChange={(e) => setNewProfileData({ ...newProfileData, url: e.target.value })}
                        className={`input-base ${!newProfileData.url && 'border-red-500/30'}`}
                        placeholder="e.g., https://mybusiness.com or facebook.com/mybusiness"
                        data-testid="profile-url-wizard"
                      />
                      <p className="text-xs text-viraly-muted mt-1">
                        We'll analyze this URL to learn about your brand and create razor-sharp content
                      </p>
                    </div>
                    
                    {/* Validation message */}
                    {(!newProfileData.name || !newProfileData.niche || !newProfileData.url) && (
                      <p className="text-xs text-red-400">
                        Please fill in all required fields to continue
                      </p>
                    )}
                    
                    <Button 
                      onClick={handleCreateProfile}
                      className="btn-primary w-full"
                      disabled={!newProfileData.name || !newProfileData.niche || !newProfileData.url}
                      data-testid="create-profile-step1-btn"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Create Profile & Analyze Brand
                    </Button>
                    
                    <p className="text-xs text-viraly-muted text-center">
                      Don't have all info? You can come back later to create this profile.
                    </p>
                  </div>
                )}
                
                {/* STEP 2: Brand Learning Confirmation */}
                {createProfileStep === 2 && (
                  <div className="space-y-4">
                    {newProfileData.brandLearningLoading ? (
                      <div className="py-12 text-center">
                        <Loader2 className="w-8 h-8 animate-spin mx-auto text-viraly-orange mb-4" />
                        <p className="text-viraly-muted">Analyzing your brand...</p>
                        <p className="text-xs text-viraly-muted mt-1">This may take a few seconds</p>
                      </div>
                    ) : newProfileData.brandLearningResult ? (
                      <>
                        <div className="p-4 rounded-xl bg-green-500/10 border border-green-500/30">
                          <div className="flex items-center gap-2 mb-2">
                            <CheckCircle className="w-5 h-5 text-green-400" />
                            <p className="font-medium text-green-400">Brand Analyzed Successfully!</p>
                          </div>
                          <p className="text-xs text-viraly-muted">From: {newProfileData.brandLearningUrl}</p>
                        </div>
                        
                        <div className="space-y-3">
                          {newProfileData.brandLearningResult.business_name && (
                            <div className="p-3 rounded-lg bg-white/5">
                              <p className="text-xs text-viraly-muted">Business Name</p>
                              <p className="font-medium">{newProfileData.brandLearningResult.business_name}</p>
                            </div>
                          )}
                          {newProfileData.brandLearningResult.brand_voice && (
                            <div className="p-3 rounded-lg bg-white/5">
                              <p className="text-xs text-viraly-muted">Brand Voice</p>
                              <p className="text-sm">{newProfileData.brandLearningResult.brand_voice.substring(0, 150)}...</p>
                            </div>
                          )}
                          {newProfileData.brandLearningResult.target_audience && (
                            <div className="p-3 rounded-lg bg-white/5">
                              <p className="text-xs text-viraly-muted">Target Audience</p>
                              <p className="text-sm">{newProfileData.brandLearningResult.target_audience.substring(0, 100)}...</p>
                            </div>
                          )}
                        </div>
                        
                        <div className="flex gap-3">
                          <Button 
                            variant="outline"
                            className="flex-1"
                            onClick={() => {
                              setNewProfileData(prev => ({ 
                                ...prev, 
                                brandLearningResult: null,
                                brandLearningUrl: ''
                              }));
                            }}
                          >
                            Try Different URL
                          </Button>
                          <Button 
                            onClick={handleConfirmBrandLearning}
                            className="btn-primary flex-1"
                          >
                            <Check className="w-4 h-4 mr-2" />
                            Confirm & Continue
                          </Button>
                        </div>
                      </>
                    ) : (
                      <>
                        <p className="text-viraly-muted text-sm">
                          Enter your business website or social URL to let Viraly learn your brand.
                        </p>
                        
                        <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-xs text-red-400">
                          <AlertCircle className="w-4 h-4 inline mr-2" />
                          <strong>Required:</strong> Brand learning is essential for creating personalized, high-quality content.
                        </div>
                        
                        <div>
                          <Input 
                            value={newProfileData.brandLearningUrl}
                            onChange={(e) => setNewProfileData({ ...newProfileData, brandLearningUrl: e.target.value })}
                            className="input-base" 
                            placeholder="https://yourbusiness.com"
                          />
                        </div>
                        
                        <Button 
                          onClick={() => handleWizardBrandLearning(newProfileData.profileId, newProfileData.brandLearningUrl)}
                          disabled={!newProfileData.brandLearningUrl}
                          className="btn-primary w-full"
                        >
                          <Sparkles className="w-4 h-4 mr-2" />
                          Analyze Brand
                        </Button>
                      </>
                    )}
                  </div>
                )}
                
                {/* STEP 3: Headshot/Logo Upload */}
                {createProfileStep === 3 && (
                  <div className="space-y-4">
                    <p className="text-viraly-muted text-sm mb-4">
                      Upload images that represent your brand. These will appear on your posts.
                    </p>
                    
                    {/* Brand Type Selection */}
                    <div className="p-4 rounded-xl bg-viraly-orange/10 border border-viraly-orange/30 mb-4">
                      <label className="text-sm font-medium mb-3 block">What represents your brand?</label>
                      <div className="grid grid-cols-2 gap-3">
                        <button
                          onClick={() => setNewProfileData(prev => ({ ...prev, brandType: 'personal' }))}
                          className={`p-3 rounded-lg border-2 transition-all ${
                            newProfileData.brandType === 'personal'
                              ? 'border-viraly-orange bg-viraly-orange/10'
                              : 'border-white/20 hover:border-white/30'
                          }`}
                        >
                          <User className="w-5 h-5 mx-auto mb-1" />
                          <p className="text-sm font-medium">Face of Brand</p>
                          <p className="text-xs text-viraly-muted">Your headshot</p>
                        </button>
                        <button
                          onClick={() => setNewProfileData(prev => ({ ...prev, brandType: 'business' }))}
                          className={`p-3 rounded-lg border-2 transition-all ${
                            newProfileData.brandType === 'business'
                              ? 'border-viraly-orange bg-viraly-orange/10'
                              : 'border-white/20 hover:border-white/30'
                          }`}
                        >
                          <Building2 className="w-5 h-5 mx-auto mb-1" />
                          <p className="text-sm font-medium">Company Logo</p>
                          <p className="text-xs text-viraly-muted">Business logo</p>
                        </button>
                      </div>
                    </div>
                    
                    {/* Image Uploads */}
                    <div className="grid grid-cols-2 gap-4">
                      {/* Headshot Upload */}
                      <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                        <p className="text-sm font-medium mb-2">Headshot/Photo</p>
                        <div className="w-20 h-20 rounded-full bg-white/10 border-2 border-dashed border-white/20 flex items-center justify-center overflow-hidden mx-auto mb-3">
                          {newProfileData.headshotPreview ? (
                            <img src={newProfileData.headshotPreview} alt="Preview" className="w-full h-full object-cover" />
                          ) : (
                            <Camera className="w-8 h-8 text-viraly-muted" />
                          )}
                        </div>
                        <label className="cursor-pointer">
                          <input type="file" accept="image/*" onChange={handleWizardHeadshotUpload} className="hidden" />
                          <div className="text-center px-4 py-2 rounded-lg border border-white/20 hover:bg-white/10 transition-colors text-sm">
                            {newProfileData.headshotPreview ? 'Change' : 'Upload'}
                          </div>
                        </label>
                      </div>
                      
                      {/* Logo Upload */}
                      <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                        <p className="text-sm font-medium mb-2">Logo</p>
                        <div className="w-20 h-20 rounded-lg bg-white/10 border-2 border-dashed border-white/20 flex items-center justify-center overflow-hidden mx-auto mb-3">
                          {newProfileData.logoPreview ? (
                            <img src={newProfileData.logoPreview} alt="Preview" className="w-full h-full object-contain p-1" />
                          ) : (
                            <Building2 className="w-8 h-8 text-viraly-muted" />
                          )}
                        </div>
                        <label className="cursor-pointer">
                          <input type="file" accept="image/*" onChange={handleWizardLogoUpload} className="hidden" />
                          <div className="text-center px-4 py-2 rounded-lg border border-white/20 hover:bg-white/10 transition-colors text-sm">
                            {newProfileData.logoPreview ? 'Change' : 'Upload'}
                          </div>
                        </label>
                      </div>
                    </div>
                    
                    {/* Required Notice */}
                    {(!newProfileData.headshotPreview && !newProfileData.logoPreview) && (
                      <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-xs text-red-400">
                        <AlertCircle className="w-4 h-4 inline mr-2" />
                        <strong>Required:</strong> Upload at least a headshot or logo for your brand profile.
                      </div>
                    )}
                    
                    <div className="flex gap-3 pt-4">
                      <Button 
                        onClick={handleSaveHeadshotLogo}
                        className="btn-primary w-full"
                        disabled={!newProfileData.headshotPreview && !newProfileData.logoPreview}
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        Save & Continue
                      </Button>
                    </div>
                  </div>
                )}
                
                {/* STEP 4: Social Account Connection */}
                {createProfileStep === 4 && (
                  <div className="space-y-4">
                    <p className="text-viraly-muted text-sm mb-4">
                      Connect social accounts to publish content directly from Viraly.
                    </p>
                    
                    {/* Pre-populated Social URL Detection */}
                    {newProfileData.detectedSocialUrl && !newProfileData.socialAccountsConfirmed && (
                      <div className="p-4 rounded-xl bg-blue-500/10 border border-blue-500/30 mb-4">
                        <div className="flex items-start gap-3">
                          <AlertCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="font-medium text-blue-400 text-sm">We detected a {newProfileData.detectedSocialUrl.platform} URL</p>
                            <p className="text-xs text-viraly-muted mt-1">{newProfileData.detectedSocialUrl.url}</p>
                            <p className="text-xs text-yellow-400 mt-2">
                              ⚠️ Please confirm you want to connect this account
                            </p>
                            <div className="flex gap-2 mt-3">
                              <Button 
                                size="sm"
                                variant="outline"
                                onClick={() => setNewProfileData(prev => ({ ...prev, detectedSocialUrl: null }))}
                              >
                                Dismiss
                              </Button>
                              <Button 
                                size="sm"
                                className="bg-blue-500 hover:bg-blue-600"
                                onClick={() => {
                                  handleConnectPlatform(newProfileData.detectedSocialUrl.platform);
                                  setNewProfileData(prev => ({ ...prev, socialAccountsConfirmed: true }));
                                }}
                              >
                                Connect {newProfileData.detectedSocialUrl.platform}
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Social Platforms List */}
                    <div className="space-y-3">
                      {[
                        { platform: 'facebook', name: 'Facebook', icon: Facebook, color: 'bg-blue-600' },
                        { platform: 'instagram', name: 'Instagram', icon: Instagram, color: 'bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400' }
                      ].map(({ platform, name, icon: Icon, color }) => (
                        <div key={platform} className="flex items-center justify-between p-3 rounded-xl bg-white/5 border border-white/10">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-xl ${color} flex items-center justify-center`}>
                              <Icon className="w-5 h-5 text-white" />
                            </div>
                            <span className="font-medium">{name}</span>
                          </div>
                          <Button
                            size="sm"
                            onClick={() => handleConnectPlatform(platform)}
                            disabled={connectingPlatform === platform}
                            className="btn-secondary"
                          >
                            {connectingPlatform === platform ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              'Connect'
                            )}
                          </Button>
                        </div>
                      ))}
                    </div>
                    
                    <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-xs text-red-400">
                      <AlertCircle className="w-4 h-4 inline mr-2" />
                      <strong>Required:</strong> Connect at least one social account to complete setup and start publishing content.
                    </div>
                    
                    <Button 
                      onClick={handleCompleteWizard}
                      className="btn-primary w-full"
                      data-testid="complete-wizard-btn"
                    >
                      <Check className="w-4 h-4 mr-2" />
                      Complete Setup
                    </Button>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Delete Profile Confirmation Modal */}
      <AnimatePresence>
        {deleteProfileId && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => {
              setDeleteProfileId(null);
              setDeleteProfileName('');
            }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-viraly-card rounded-2xl w-full max-w-md overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center">
                    <Trash2 className="w-6 h-6 text-red-400" />
                  </div>
                  <div>
                    <h2 className="font-heading text-lg font-bold">Delete Profile</h2>
                    <p className="text-sm text-viraly-muted">This action cannot be undone</p>
                  </div>
                </div>
                
                <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/20 mb-6">
                  <p className="text-sm text-red-300">
                    Are you sure you want to delete <strong>"{deleteProfileName}"</strong>? 
                    This will remove all associated brand learning, social account connections, 
                    and content linked to this profile.
                  </p>
                </div>
                
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => {
                      setDeleteProfileId(null);
                      setDeleteProfileName('');
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    className="flex-1 bg-red-500 hover:bg-red-600 text-white"
                    onClick={handleDeleteProfile}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete Profile
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Connection Success Modal */}
      <ConnectionSuccessModal
        isOpen={connectionSuccessModal.isOpen}
        onClose={() => setConnectionSuccessModal({ isOpen: false, platform: null, accountName: "", accountHandle: "" })}
        platform={connectionSuccessModal.platform}
        accountName={connectionSuccessModal.accountName}
        accountHandle={connectionSuccessModal.accountHandle}
        onStartCreating={() => {
          setConnectionSuccessModal({ isOpen: false, platform: null, accountName: "", accountHandle: "" });
          navigate("/create");
        }}
      />
      
      {/* Disconnect Confirm Modal */}
      <DisconnectConfirmModal
        isOpen={disconnectModal.isOpen}
        onClose={() => setDisconnectModal({ isOpen: false, platform: null, accountId: null, accountName: "" })}
        platform={disconnectModal.platform}
        accountName={disconnectModal.accountName}
        onConfirm={async () => {
          if (disconnectModal.accountId) {
            try {
              await apiClient.delete(`/social/${disconnectModal.accountId}`);
              toast.success(`${disconnectModal.platform} disconnected`);
              fetchSocialStatus();
              if (activeProfile?.id) {
                fetchProfileSocialStatus();
              }
            } catch (error) {
              toast.error(`Failed to disconnect ${disconnectModal.platform}`);
            }
          }
          setDisconnectModal({ isOpen: false, platform: null, accountId: null, accountName: "" });
        }}
      />
      
      {/* Downgrade Profile Selection Modal */}
      <DowngradeProfileSelectionModal
        isOpen={showDowngradeModal}
        onClose={() => setShowDowngradeModal(false)}
        profiles={brandProfiles.filter(p => p.is_active !== false)}
        allowedCount={profileLimit}
        onConfirm={handleDowngradeProfileSelection}
      />

      {/* OAuth Connect Modal - Beautiful Viraly-branded */}
      <AnimatePresence>
        {oauthModal.isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setOauthModal({ isOpen: false, platform: null, connectUrl: null, loading: false })}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="bg-gradient-to-b from-viraly-card to-viraly-dark rounded-2xl w-full max-w-md overflow-hidden shadow-2xl border border-white/10"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header with platform branding */}
              <div className="p-6 text-center border-b border-white/10">
                <div className="mx-auto w-16 h-16 rounded-2xl bg-gradient-to-br from-viraly-orange to-viraly-accent flex items-center justify-center mb-4 shadow-lg">
                  {oauthModal.platform === 'facebook' && <Facebook className="w-8 h-8 text-white" />}
                  {oauthModal.platform === 'instagram' && <Instagram className="w-8 h-8 text-white" />}
                  {oauthModal.platform === 'tiktok' && (
                    <svg className="w-8 h-8 text-white" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
                    </svg>
                  )}
                  {oauthModal.platform === 'pinterest' && (
                    <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 0C5.373 0 0 5.373 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738.098.119.112.224.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.632-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z"/>
                    </svg>
                  )}
                  {oauthModal.platform === 'linkedin' && (
                    <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                    </svg>
                  )}
                  {oauthModal.platform === 'youtube' && (
                    <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                    </svg>
                  )}
                  {oauthModal.platform === 'twitter' && (
                    <svg className="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                    </svg>
                  )}
                </div>
                <h3 className="text-xl font-heading font-bold">
                  Connect {oauthModal.platform?.charAt(0).toUpperCase() + oauthModal.platform?.slice(1)}
                </h3>
                <p className="text-sm text-viraly-muted mt-2">
                  Securely connect your account to enable auto-posting
                </p>
              </div>

              {/* Content */}
              <div className="p-6 space-y-4">
                <div className="space-y-3">
                  <div className="flex items-center gap-3 text-sm">
                    <div className="w-6 h-6 rounded-full bg-viraly-success/20 flex items-center justify-center flex-shrink-0">
                      <Check className="w-3.5 h-3.5 text-viraly-success" />
                    </div>
                    <span className="text-viraly-muted">One-time authorization</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <div className="w-6 h-6 rounded-full bg-viraly-success/20 flex items-center justify-center flex-shrink-0">
                      <Check className="w-3.5 h-3.5 text-viraly-success" />
                    </div>
                    <span className="text-viraly-muted">Secure OAuth 2.0 connection</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <div className="w-6 h-6 rounded-full bg-viraly-success/20 flex items-center justify-center flex-shrink-0">
                      <Check className="w-3.5 h-3.5 text-viraly-success" />
                    </div>
                    <span className="text-viraly-muted">Disconnect anytime from Settings</span>
                  </div>
                </div>

                {oauthModal.platform === 'tiktok' && (
                  <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                    <div className="flex items-start gap-2">
                      <Info className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-amber-200">
                        TikTok requires approval for each post due to their API guidelines. You'll receive a notification before each TikTok post.
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="p-6 pt-0 flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1 border-white/20"
                  onClick={() => setOauthModal({ isOpen: false, platform: null, connectUrl: null, loading: false })}
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1 btn-primary"
                  onClick={openOAuthPopup}
                  disabled={!oauthModal.connectUrl}
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Connect Account
                </Button>
              </div>

              {/* Footer */}
              <div className="px-6 pb-6">
                <p className="text-xs text-center text-viraly-muted">
                  By connecting, you agree to our Terms of Service and Privacy Policy
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </DashboardLayout>
  );
}
